import React, { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import {
  Newspaper,
  RefreshCw,
  Send,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Users,
  FileText,
  TrendingUp,
  Clock,
  Moon,
  Sun,
  Mail,
  Sparkles,
  Zap,
  ArrowRight,
  ChevronDown,
  Calendar,
  PenLine,
  Copy,
  Linkedin,
  Twitter,
  BookOpen,
  Share2,
  Check,
  Wrench,
  DollarSign,
  Target,
  ListChecks,
  GitCompare,
  Rocket,
  AlertTriangle,
  Terminal,
  Smile,
  Palette,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { getAttributionPayload, initAttribution } from "@/lib/attribution";

// API URL - uses localhost:8001 for development, otherwise uses same origin as page
const getApiUrl = () => {
  if (typeof window === 'undefined') return 'http://localhost:8001/api';
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001/api';
  }
  return `${window.location.origin}/api`;
};
const API = getApiUrl();

// Theme Toggle Hook
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  return [isDark, setIsDark];
};

// Helper for cleaning up text for social sharing
const cleanForSocial = (content) => {
  if (!content) return '';
  return content
    .replace(/^(?:\*\*)?(?:HOOK|THE HOOK|Hook|CONTEXT|Context|BACKGROUND|Background)[:;]?(?:\*\*)?\s*/gmi, '')
    .replace(/^(?:\*\*)?(?:The takeaway|Takeaway|Why it matters|WHY IT MATTERS|Insight)[:;]?(?:\*\*)?\s*/gmi, '💡 Takeaway: ')
    .replace(/^(?:\*\*)?(?:CTA|Cta|Question)[:;]?(?:\*\*)?\s*/gmi, '')
    .replace(/\*\*/g, '')
    .replace(/Share on X/gi, '')
    .trim();
};

// Extract hook and takeaway from AI-generated brief/editorial content.
const extractHookAndTakeaway = (content) => {
  if (!content) return { hook: '', takeaway: '', body: '' };
  const paragraphs = content.split(/\n\n+/).filter(Boolean).map((p) => p.trim());
  let hook = '';
  let takeaway = '';
  const body = [];

  paragraphs.forEach((text) => {
    const lower = text.toLowerCase();
    if (/^(?:\*\*)?(?:the takeaway|takeaway|why it matters|insight|executive summary)[:;]?(?:\*\*)?\s*/i.test(text)) {
      takeaway = text.replace(/^(?:\*\*)?(?:the takeaway|takeaway|why it matters|insight|executive summary)[:;]?(?:\*\*)?\s*/i, '').trim();
    } else if (/^(?:\*\*)?(?:hook|the hook)[:;]?(?:\*\*)?\s*/i.test(text)) {
      hook = text.replace(/^(?:\*\*)?(?:hook|the hook)[:;]?(?:\*\*)?\s*/i, '').trim();
    } else if (/share on x/i.test(text)) {
      // Ignore this artifact
    } else {
      body.push(text.replace(/^(?:\*\*)?(?:context|background|cta|question)[:;]?(?:\*\*)?\s*/i, '').trim());
    }
  });

  // If no explicit hook, use the first non-takeaway paragraph
  if (!hook && body.length > 0) {
    hook = body[0];
  }
  // If no explicit takeaway, use the last paragraph
  if (!takeaway && body.length > 1) {
    takeaway = body[body.length - 1];
  }

  const strip = (str) => (str ? str.replace(/\*\*/g, '').trim() : '');
  return { hook: strip(hook), takeaway: strip(takeaway), body: body.map(strip).join('\n\n') };
};

// Build a short, punchy LinkedIn caption: 1-2 sentence hook + link.
const buildLinkedInCaption = ({ type, title, content, summary, url, hook }) => {
  let snippet = '';
  if (hook) {
    snippet = hook.replace(/\*\*/g, '').trim();
  } else {
    const { hook: extractedHook, takeaway } = content ? extractHookAndTakeaway(content) : { hook: summary || '', takeaway: '' };
    snippet = extractedHook || takeaway || summary || '';
    snippet = snippet.replace(/\*\*/g, '').trim();
  }
  // Keep it to ~2 short sentences / 200 chars so it reads like a social hook.
  const sentences = snippet.match(/[^.!?]+[.!?]+/g) || [snippet];
  snippet = sentences.slice(0, 2).join(' ').trim();
  if (snippet.length > 200) {
    snippet = snippet.substring(0, 197).trim() + '...';
  }
  let caption = '';
  if (title) {
    caption += `${title}\n\n`;
  }
  if (snippet) {
    caption += `${snippet}\n\n`;
  }
  caption += `Read the full ${type === 'editorial' ? 'analysis' : 'brief'} → ${url}`;
  return caption.trim();
};

// Build a rich caption for the share buttons (X, LinkedIn, Threads, etc.)
const buildShareCaption = ({ type, title, summary, url, cta = 'Read more' }) => {
  const labels = {
    article: '📰',
    comic: '🎨 Neural Toon',
    snap: '⚡ Neural Snap',
    brief: '📰 AI Brief',
    editorial: '📊 Editorial',
    toolguide: '🔧 Tool Guide',
  };
  const label = labels[type] || '📰';
  const ctaText = type === 'comic' ? 'Check it out' : cta;

  let caption = '';
  if (type === 'comic') {
    caption = `${label}: ${title}\n\n${summary}\n\n${ctaText}: ${url}`;
  } else if (title) {
    caption = `${label} ${title}\n\n${summary}\n\n${ctaText}: ${url}`;
  } else {
    caption = `${label}\n\n${summary}\n\n${ctaText}: ${url}`;
  }
  return caption.trim();
};

// Render inline **bold** markdown as <strong>
const renderMarkdownBold = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
};

// Formatted Brief Component — supports structured hook/body/takeaways/closing
const FormattedBrief = ({ content, title, hook, keyTakeaways, closing }) => {
  if (!content && !hook && !keyTakeaways?.length && !closing) return null;

  // Use structured fields if provided; otherwise parse legacy flat content
  let bodyParagraphs = [];
  let parsedTakeaways = keyTakeaways || [];

  // Normalize content: replace <br> tags with newlines before parsing
  const normalizedContent = (content || '').replace(/<br\s*\/?>/gi, '\n');

  if (hook || keyTakeaways?.length || closing) {
    // Structured mode: content is the body + possibly a repeated takeaways block
    bodyParagraphs = normalizedContent
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter((p) => p && !p.startsWith('Key Takeaways:') && !p.startsWith('- ') && p !== hook && p !== closing);
  } else {
    // Legacy parsing mode
    const paragraphs = normalizedContent.split(/\n\n+/).filter(Boolean).map((p) => p.trim());
    let legacyTakeaway = null;

    paragraphs.forEach((text) => {
      if (/^(?:\*\*)?(?:The takeaway|Takeaway|Why it matters|WHY IT MATTERS|Insight|Executive Summary)[:;]?(?:\*\*)?\s*/i.test(text)) {
        legacyTakeaway = text.replace(/^(?:\*\*)?(?:The takeaway|Takeaway|Why it matters|WHY IT MATTERS|Insight|Executive Summary)[:;]?(?:\*\*)?\s*/i, '').trim();
      } else if (/Share on X/i.test(text)) {
        // Ignore this artifact entirely
      } else {
        let cleanedText = text.replace(/^(?:\*\*)?(?:HOOK|Hook|THE HOOK|The hook|CONTEXT|Context|BACKGROUND|Background|CTA|Cta|Question)[:;]?(?:\*\*)?\s*/i, '').trim();
        bodyParagraphs.push(cleanedText);
      }
    });

    if (!legacyTakeaway && bodyParagraphs.length > 2) {
      legacyTakeaway = bodyParagraphs.pop().replace(/^\*\*.*?\*\*\s*/, '').trim();
    }
    if (legacyTakeaway) parsedTakeaways = [legacyTakeaway];
  }

  return (
    <div className="space-y-5">
      {title && (
        <h3 className="text-xl font-bold text-foreground leading-snug tracking-tight mb-2">
          {title}
        </h3>
      )}

      {hook && (
        <p className="text-lg md:text-xl font-bold text-foreground leading-snug border-l-4 border-primary pl-4">
          {renderMarkdownBold(hook)}
        </p>
      )}

      {bodyParagraphs.length > 0 && (
        <div className="space-y-4 text-[15px] leading-relaxed text-foreground/90">
          {bodyParagraphs.map((p, i) => (
            <p key={i}>{renderMarkdownBold(p)}</p>
          ))}
        </div>
      )}

      {parsedTakeaways.length > 0 && (
        <div className="mt-5 bg-muted/40 p-5 rounded-xl border border-border">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-primary" />
            <h4 className="text-foreground font-bold text-[11px] uppercase tracking-widest">
              Key Takeaways
            </h4>
          </div>
          <ul className="space-y-2.5">
            {parsedTakeaways.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-[14px] text-foreground/80 font-medium leading-relaxed">
                <span className="text-primary mt-1.5">•</span>
                <span>{renderMarkdownBold(t)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {closing && (
        <p className="text-[15px] font-medium text-foreground/90 italic border-t border-border pt-4">
          {renderMarkdownBold(closing)}
        </p>
      )}
    </div>
  );
};


// Comic Card Component for News Grid and Comics Gallery
const ComicCard = ({ comic, onClick, onReadArticleClick }) => {
  return (
    <Card
      data-testid={`comic-card-${comic.short_id}`}
      className="group cursor-pointer border-2 border-primary/10 hover:border-primary/30 bg-card/65 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl overflow-hidden flex flex-col h-full"
      onClick={() => onClick(comic)}
    >
      {/* Visual Comic Strip Image Frame */}
      <div className="relative overflow-hidden aspect-square bg-muted border-b border-border">
        {/* Glow effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10 opacity-70 group-hover:opacity-45 transition-opacity duration-300" />
        <img
          src={comic.public_url}
          alt={comic.headline}
          className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500 ease-out select-none"
          loading="lazy"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
        {/* Category-like Comic Badge */}
        <div className="absolute top-3 left-3 z-20 flex gap-1.5">
          <Badge className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 border-none shadow-sm flex items-center gap-1">
            <Smile className="w-2.5 h-2.5" />
            Neural Toon
          </Badge>
          <Badge variant="secondary" className="font-mono text-[9px] bg-background/80 backdrop-blur-sm shadow-sm text-foreground/80 px-2 py-0.5 border border-border">
            {comic.character_pair || 'AI Cast'}
          </Badge>
        </div>
      </div>

      {/* Comic content body */}
      <CardContent className="p-4 flex flex-col justify-between flex-grow">
        <div className="mb-3">
          <h3 className="font-bold text-base leading-snug mb-1.5 group-hover:text-accent transition-colors line-clamp-2">
            {comic.headline}
          </h3>
          {comic.punchline && (
            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 italic">
              💬 "{comic.punchline}"
            </p>
          )}
        </div>

        {/* Comic Footer action and credits */}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-2.5 border-t border-border/50 mt-auto">
          <span className="font-mono flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {comic.created_at ? formatDistanceToNow(new Date(comic.created_at), { addSuffix: true }) : 'Recently'}
          </span>
          <div className="flex items-center gap-2">
            {onReadArticleClick && comic.article_id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReadArticleClick(comic.article_id);
                }}
                className="text-[10px] font-semibold text-accent border border-accent/20 rounded-full px-2.5 py-0.5 hover:bg-accent hover:text-white transition-all"
              >
                Full Story
              </button>
            )}
            <span className="text-accent group-hover:translate-x-0.5 transition-transform flex items-center font-semibold">
              View <ArrowRight className="w-3 h-3 ml-0.5" />
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


// Neural Snap Card Component for News Grid and Neural Snaps Gallery
const SnapCard = ({ snap, onClick, onReadStoryClick }) => {
  const snapImgSrc = snap.image_url ? `${snap.image_url}${snap.image_url.includes('?') ? '&' : '?'}v=r2` : '';
  return (
    <Card
      data-testid={`snap-card-${snap.id}`}
      className="group cursor-pointer border-2 border-emerald-500/15 hover:border-emerald-500/50 bg-card/75 backdrop-blur-md transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl overflow-hidden flex flex-col h-full"
      onClick={() => onClick(snap)}
    >
      {/* 4:5 Visual Snap Image Frame (Protected) */}
      <div 
        className="relative overflow-hidden aspect-[4/5] bg-black border-b border-border select-none"
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent z-10 opacity-60 group-hover:opacity-30 transition-opacity duration-300 pointer-events-none" />
        <img
          src={snapImgSrc}
          alt={snap.headline}
          className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500 ease-out select-none pointer-events-none"
          loading="lazy"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
        {/* Anti-copy shield overlay */}
        <div 
          className="absolute inset-0 bg-transparent select-none z-10" 
          onContextMenu={(e) => e.preventDefault()} 
          draggable={false} 
        />
        {/* Category & Badge */}
        <div className="absolute top-3 left-3 z-20 flex gap-1.5 pointer-events-none">
          <Badge className="bg-emerald-500 hover:bg-emerald-600 text-black font-mono font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 border-none shadow-md flex items-center gap-1">
            <Zap className="w-2.5 h-2.5 fill-current" />
            Neural Snap
          </Badge>
          {snap.category && (
            <Badge variant="secondary" className="font-mono text-[9px] bg-black/70 backdrop-blur-sm shadow-sm text-white/90 px-2 py-0.5 border border-white/10">
              {snap.category}
            </Badge>
          )}
        </div>
      </div>

      {/* Snap Content Body */}
      <CardContent className="p-4 flex flex-col justify-between flex-grow">
        <div className="mb-3">
          <h3 className="font-bold text-base leading-snug mb-1.5 group-hover:text-emerald-400 transition-colors line-clamp-2">
            {snap.headline}
          </h3>
          {snap.punchline && (
            <p className="text-xs text-emerald-400/90 font-medium leading-relaxed line-clamp-2">
              💥 {snap.punchline}
            </p>
          )}
        </div>

        {/* Snap Footer */}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-2.5 border-t border-border/50 mt-auto">
          <span className="font-mono flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {snap.created_at ? formatDistanceToNow(new Date(snap.created_at), { addSuffix: true }) : 'Today'}
          </span>
          <div className="flex items-center gap-2">
            {(snap.editorial_id || snap.article_id || snap.article_url || (snap.sources && snap.sources.length > 0)) && onReadStoryClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReadStoryClick(snap);
                }}
                className="text-[10px] font-semibold text-emerald-400 border border-emerald-500/30 rounded-full px-2.5 py-0.5 hover:bg-emerald-500 hover:text-black transition-all flex items-center gap-1"
              >
                Full Story <ArrowRight className="w-2.5 h-2.5" />
              </button>
            )}
            <span className="text-foreground group-hover:translate-x-0.5 transition-transform flex items-center font-semibold">
              View <ArrowRight className="w-3 h-3 ml-0.5" />
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


// Article Card Component with Hook + Insight design
const ArticleCard = ({ article, onClick, relatedGuide, onToolGuideClick }) => {
  const getCategoryColor = (category) => {
    const colors = {
      'Models & Releases': 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
      'Research & Papers': 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
      'Developer Tools': 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
      'Open Source': 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
      'Startups & Funding': 'bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20',
      'Enterprise & Industry': 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
      'Agent Frameworks': 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
      'Image & Video': 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
      'Hardware & Chips': 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
      'Regulation & Policy': 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
      'AI Safety': 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
      'Audio & Speech': 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
      'Audio & Music': 'bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20',
      'Robotics': 'bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20',
      'Opinion & Analysis': 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
      'Media & Culture': 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400 border-fuchsia-500/20',
    };
    return colors[category] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20';
  };

  const getPriorityBadge = (tier) => {
    const badges = {
      'VIRAL': 'bg-gradient-to-r from-red-500 to-pink-500 text-white',
      'HOT': 'bg-orange-500 text-white',
      'TOP': 'bg-blue-600 text-white',
      'HIGH': 'bg-blue-500 text-white',
      'GOOD': 'bg-gray-500 text-white',
    };
    return badges[tier] || badges['GOOD'];
  };

  return (
    <Card
      data-testid={`article-card-${article.id}`}
      className="group cursor-pointer border border-border bg-card hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg overflow-hidden"
      onClick={() => onClick(article)}
    >
      <CardContent className="p-0">
        {/* Meta badges at top */}

        <div className="p-5">
          {/* Meta info */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {article.priority_tier && (
              <Badge className={`font-mono text-[10px] px-1.5 py-0 ${getPriorityBadge(article.priority_tier)}`}>
                {article.priority_tier === 'VIRAL' && <Sparkles className="w-3 h-3 mr-1" />}
                {article.priority_tier}
              </Badge>
            )}
            <Badge variant="outline" className={`font-mono text-[10px] ${getCategoryColor(article.category)}`}>
              {article.category}
            </Badge>
            {relatedGuide && onToolGuideClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToolGuideClick(relatedGuide);
                }}
                className="inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
              >
                <Wrench className="w-2.5 h-2.5" />
                Tool Guide
              </button>
            )}
          </div>

          {/* MAIN HEADLINE - Hook (simplified) or fallback */}
          <h3 className="font-bold text-lg leading-tight mb-2 group-hover:text-accent transition-colors line-clamp-2">
            <Zap className="w-4 h-4 text-accent inline-block mr-1.5 mb-0.5" />
            {article.hook || article.simplified_title || article.title}
          </h3>

          {/* Technical title - small, secondary reference */}
          {article.hook && article.title && (
            <p className="text-xs text-muted-foreground/70 mb-3 line-clamp-1 italic">
              Technical: {article.title}
            </p>
          )}

          {/* Summary - The insight */}
          <p className="text-sm text-muted-foreground leading-relaxed mb-3 line-clamp-3">
            {article.summary || 'Loading summary...'}
          </p>

          {/* Why it matters */}
          {article.why_it_matters && (
            <div className="bg-muted/50 rounded-md px-3 py-2 mb-3">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Why it matters:</span> {article.why_it_matters}
              </p>
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
          <div className="flex items-center gap-2">
            <span className="font-mono">{article.source_name}</span>
            <span>•</span>
            <span className="flex items-center gap-1" title={article.published_at || article.scraped_at}>
              <Clock className="w-3 h-3" />
              {article.published_at
                ? formatDistanceToNow(new Date(article.published_at), { addSuffix: true })
                : article.scraped_at
                  ? formatDistanceToNow(new Date(article.scraped_at), { addSuffix: true })
                  : 'Recent'}
            </span>
          </div>
          <div className="flex items-center gap-1 text-accent opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-xs font-medium">Read more</span>
            <ArrowRight className="w-3 h-3" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value }) => (
  <Card data-testid={`stats-${label.toLowerCase().replace(/\s/g, '-')}`} className="border border-border">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
          <p className="text-2xl font-black tracking-tight">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Create Guide Form Component
const CreateGuideForm = ({ onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    tool_name: '',
    title: '',
    category: 'Developer Tools',
    difficulty: 'Intermediate',
    overview: '',
    tool_url: '',
    steps: [{ step_number: 1, instruction: '' }],
    pro_tips: [''],
    use_cases: [''],
    pricing: '',
    caveats: '',
    verdict: '',
    source_article_id: '',
    source_article_title: '',
    source_article_url: ''
  });

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));
  
  const updateStep = (i, value) => {
    const newSteps = [...form.steps];
    newSteps[i] = { ...newSteps[i], instruction: value };
    setForm(f => ({ ...f, steps: newSteps }));
  };
  
  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { step_number: f.steps.length + 1, instruction: '' }] }));
  
  const removeStep = (i) => {
    const newSteps = form.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step_number: idx + 1 }));
    setForm(f => ({ ...f, steps: newSteps }));
  };

  const updateList = (field, i, value) => {
    const newList = [...form[field]];
    newList[i] = value;
    setForm(f => ({ ...f, [field]: newList }));
  };
  
  const addListItem = (field) => setForm(f => ({ ...f, [field]: [...f[field], ''] }));
  
  const removeListItem = (field, i) => setForm(f => ({ ...f, [field]: f[field].filter((_, idx) => idx !== i) }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      steps: form.steps.filter(s => s.instruction.trim()),
      pro_tips: form.pro_tips.filter(t => t.trim()),
      use_cases: form.use_cases.filter(u => u.trim())
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium mb-1 block">Tool Name</label>
          <Input value={form.tool_name} onChange={e => updateField('tool_name', e.target.value)} placeholder="e.g. Cursor" required />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Title</label>
          <Input value={form.title} onChange={e => updateField('title', e.target.value)} placeholder="e.g. How I use Cursor to ship 2x faster" required />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium mb-1 block">Category</label>
          <select value={form.category} onChange={e => updateField('category', e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
            <option>Developer Tools</option>
            <option>Image & Video</option>
            <option>Audio & Speech</option>
            <option>Productivity</option>
            <option>Research</option>
            <option>Creative</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Difficulty</label>
          <select value={form.difficulty} onChange={e => updateField('difficulty', e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
            <option>Beginner</option>
            <option>Intermediate</option>
            <option>Advanced</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block">Tool URL</label>
          <Input value={form.tool_url} onChange={e => updateField('tool_url', e.target.value)} placeholder="https://..." />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block">Overview (pain point, what it does, who cares, first impression, workflow, surprise, who should skip)</label>
        <textarea value={form.overview} onChange={e => updateField('overview', e.target.value)} rows={6} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Write short paragraphs. Use line breaks." required />
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block">Steps</label>
        <div className="space-y-2">
          {form.steps.map((step, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-xs font-mono text-muted-foreground w-6 pt-2">{i+1}</span>
              <Input value={step.instruction} onChange={e => updateStep(i, e.target.value)} placeholder={`Step ${i+1} instruction`} className="flex-1" />
              {form.steps.length > 1 && (
                <button type="button" onClick={() => removeStep(i)} className="text-xs text-red-500 hover:text-red-400 px-2">✕</button>
              )}
            </div>
          ))}
          <button type="button" onClick={addStep} className="text-xs text-primary hover:text-primary/80 font-medium">+ Add Step</button>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block">Pro Tips</label>
        <div className="space-y-2">
          {form.pro_tips.map((tip, i) => (
            <div key={i} className="flex gap-2">
              <Input value={tip} onChange={e => updateList('pro_tips', i, e.target.value)} placeholder={`Tip ${i+1}`} className="flex-1" />
              <button type="button" onClick={() => removeListItem('pro_tips', i)} className="text-xs text-red-500 hover:text-red-400 px-2">✕</button>
            </div>
          ))}
          <button type="button" onClick={() => addListItem('pro_tips')} className="text-xs text-primary hover:text-primary/80 font-medium">+ Add Tip</button>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block">Use Cases</label>
        <div className="space-y-2">
          {form.use_cases.map((uc, i) => (
            <div key={i} className="flex gap-2">
              <Input value={uc} onChange={e => updateList('use_cases', i, e.target.value)} placeholder={`Use case ${i+1}`} className="flex-1" />
              <button type="button" onClick={() => removeListItem('use_cases', i)} className="text-xs text-red-500 hover:text-red-400 px-2">✕</button>
            </div>
          ))}
          <button type="button" onClick={() => addListItem('use_cases')} className="text-xs text-primary hover:text-primary/80 font-medium">+ Add Use Case</button>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block">Pricing Reality</label>
        <textarea value={form.pricing} onChange={e => updateField('pricing', e.target.value)} rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Free tier, paid tier, hidden cost, bottom line" />
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block">The Catch (limitations)</label>
        <textarea value={form.caveats} onChange={e => updateField('caveats', e.target.value)} rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="What breaks, when it disappoints, workflow gaps" />
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block">The Verdict</label>
        <textarea value={form.verdict} onChange={e => updateField('verdict', e.target.value)} rows={2} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Who should use it, who should wait, switch scenario" />
      </div>

      <div className="border-t border-border pt-4">
        <label className="text-xs font-medium mb-1 block text-muted-foreground">Link to Source Article (optional)</label>
        <div className="grid grid-cols-2 gap-3">
          <Input value={form.source_article_title} onChange={e => updateField('source_article_title', e.target.value)} placeholder="Article title" />
          <Input value={form.source_article_url} onChange={e => updateField('source_article_url', e.target.value)} placeholder="Article URL" />
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" className="rounded-full flex-1">Publish Guide</Button>
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
      </div>
    </form>
  );
};

// Brief Preview Component - shows excerpt with Read More
const BriefPreview = ({ content, title, hook, onReadMore }) => {
  const normalizedContent = (content || '').replace(/<br\s*\/?>/gi, '\n');
  const paragraphs = normalizedContent.split(/\n\n+/).filter(Boolean).map(p => p.trim());
  const previewParagraphs = hook ? paragraphs.slice(0, 1) : paragraphs.slice(0, 2);
  const hasContent = paragraphs.length > 0 && previewParagraphs.some(p => p.length > 5);
  const hasMore = paragraphs.length > (hook ? 1 : 2) || previewParagraphs.some(p => p.length > 180);

  return (
    <div>
      {title && (
        <h3 className="text-base font-bold text-foreground leading-snug mb-2">
          {title}
        </h3>
      )}
      {hook && (
        <p className="text-sm font-semibold text-foreground leading-relaxed mb-3 border-l-2 border-primary pl-3">
          {hook.replace(/\*\*/g, '')}
        </p>
      )}
      {hasContent ? (
        <>
          <div className="space-y-3 text-sm leading-relaxed text-foreground/90">
            {previewParagraphs.map((p, i) => (
              <p key={i} className="line-clamp-3">{p.replace(/\*\*/g, '')}</p>
            ))}
          </div>
          {hasMore && (
            <button
              onClick={onReadMore}
              className="mt-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
            >
              Read full brief <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </>
      ) : (
        <button
          onClick={onReadMore}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
        >
          Read full brief <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// Editorial Card Component
function EditorialCard({ editorial, onClick, isBest }) {
  return (
    <Card
      className={`group border overflow-hidden cursor-pointer hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 ${isBest ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-border'}`}
      onClick={() => onClick(editorial)}
    >
      <div className={`px-5 py-3 border-b border-border/50 ${isBest ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/10' : 'bg-gradient-to-r from-primary/10 to-accent/10'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PenLine className={`w-3.5 h-3.5 ${isBest ? 'text-yellow-500' : 'text-primary'}`} />
            <span className={`text-[10px] font-mono font-bold uppercase tracking-wider ${isBest ? 'text-yellow-500' : 'text-primary'}`}>
              {isBest ? 'Best Pick' : 'Editorial'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isBest && editorial.quality_score && (
              <span className="text-[10px] font-bold text-yellow-500">★ {editorial.quality_score}</span>
            )}
            <BookOpen className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">{editorial.reading_time_min} min read</span>
          </div>
        </div>
      </div>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="outline" className="text-[10px] font-mono">{editorial.category}</Badge>
          {editorial.tags?.slice(0, 2).map((tag, i) => (
            <span key={i} className="text-[10px] text-muted-foreground">#{tag}</span>
          ))}
        </div>
        <h3 className="text-base font-bold leading-tight mb-2 group-hover:text-primary transition-colors">
          {editorial.headline}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-3 line-clamp-3">
          {editorial.subheadline}
        </p>
        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          <span className="text-[10px] text-muted-foreground">
            ✍️ By The Neural Feed {editorial.week_of ? `· ${editorial.week_of}` : ''}
          </span>
          <span className="text-xs text-primary font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
            Read <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Dashboard Component
// SVG icons for social platforms not in lucide-react
const InstagramIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const ThreadsIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.716 5.496 2.057 7.164 1.432 1.781 3.632 2.695 6.54 2.717 2.673-.02 4.65-.93 6.04-2.758.914-1.18 1.38-2.682 1.387-4.464V13.04H12.19v-1.817h8.342v1.333c-.007 2.277-.612 4.32-1.799 6.073-1.788 2.615-4.566 3.996-8.257 4.015h-.288z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.399.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
  </svg>
);

const SocialShareBar = ({ url, text, linkedinText, imageUrl, className = "" }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedImage = imageUrl ? encodeURIComponent(imageUrl) : '';
  // Twitter counts URLs as 23 chars; keep text + URL under 280.
  const twitterText = text.length > 250 ? text.substring(0, 247) + '...' : text;
  const encodedText = encodeURIComponent(twitterText);
  const encodedFullText = encodeURIComponent(text);
  const linkedInCaption = (linkedinText || text).trim();
  const encodedLinkedInText = encodeURIComponent(linkedInCaption);

  const handleCopy = async (platform, label, content) => {
    try {
      await navigator.clipboard.writeText(content || url);
      toast.success(`${label} caption copied! Paste it into ${platform}.`);
    } catch (e) {
      toast.error('Could not copy caption.');
    }
  };

  const shareLinkedIn = async () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // For native share, don't duplicate the URL — the share sheet adds it separately.
      const arrowIndex = linkedInCaption.lastIndexOf('→');
      const linkedInTextOnly = arrowIndex > 0 ? linkedInCaption.substring(0, arrowIndex).trim() : linkedInCaption;
      // Try the native share sheet first — many Android devices pass the text through to LinkedIn.
      const shareData = { title: linkedInTextOnly.split('\n')[0], text: linkedInTextOnly, url };
      if (navigator.canShare && navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
          return;
        } catch (err) {
          // User cancelled or sharing failed — fall back to copy + open LinkedIn.
        }
      }
      // Fallback: copy full caption (with URL) and open LinkedIn web share so the user can paste.
      try {
        await navigator.clipboard.writeText(linkedInCaption);
        toast.success('LinkedIn caption copied. Paste it after LinkedIn opens.');
      } catch (e) {
        toast.error('Could not copy LinkedIn caption.');
      }
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank', 'noopener,noreferrer');
      return;
    }
    // Desktop: LinkedIn web composer pre-fills the caption + URL.
    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodedLinkedInText}`, '_blank', 'noopener,noreferrer');
  };

  // Try the native Web Share API first (mobile system share sheet).
  // Falls back to the provided function on desktop, old browsers, or if the user cancels.
  const shareNative = async (fallback) => {
    const shareData = { title: text.split('\n')[0], text, url };
    if (navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // User cancelled or sharing failed — fall back.
      }
    }
    fallback();
  };

  const sharePinterest = () => {
    shareNative(() => {
      window.open(`https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedFullText}`, '_blank', 'noopener,noreferrer');
    });
  };

  const shareInstagram = () => {
    shareNative(() => handleCopy('Instagram', 'Instagram', text));
  };

  const shareTikTok = () => {
    shareNative(() => handleCopy('TikTok', 'TikTok', text));
  };

  const shareThreads = () => {
    shareNative(() => handleCopy('Threads', 'Threads', text));
  };

  const platforms = [
    {
      key: 'x',
      label: 'X',
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&hashtags=AI,Tech,NeuralFeed`,
      icon: <Twitter className="w-3.5 h-3.5" />,
      style: 'bg-black hover:bg-black/80 text-white dark:bg-white dark:text-black'
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      onClick: shareLinkedIn,
      icon: <Linkedin className="w-3.5 h-3.5" />,
      style: 'bg-[#0A66C2] hover:bg-[#0842a0] text-white'
    },
    {
      key: 'pinterest',
      label: 'Pinterest',
      onClick: sharePinterest,
      icon: <PinterestIcon />,
      style: 'bg-[#E60023] hover:bg-[#bd001c] text-white'
    },
    {
      key: 'instagram',
      label: 'Instagram',
      onClick: shareInstagram,
      icon: <InstagramIcon />,
      style: 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 hover:opacity-90 text-white'
    },
    {
      key: 'tiktok',
      label: 'TikTok',
      onClick: shareTikTok,
      icon: <TikTokIcon />,
      style: 'bg-black hover:bg-black/80 text-white dark:bg-white dark:text-black'
    },
    {
      key: 'threads',
      label: 'Threads',
      onClick: shareThreads,
      icon: <ThreadsIcon />,
      style: 'bg-black hover:bg-black/80 text-white dark:bg-white dark:text-black'
    }
  ];

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {platforms.map((p) =>
        p.href ? (
          <a
            key={p.key}
            href={p.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center p-2 rounded-full transition-all active:scale-95 ${p.style}`}
            title={`Share on ${p.label}`}
          >
            {p.icon}
          </a>
        ) : (
          <button
            key={p.key}
            onClick={p.onClick}
            className={`inline-flex items-center justify-center p-2 rounded-full transition-all active:scale-95 ${p.style}`}
            title={`Copy link for ${p.label}`}
          >
            {p.icon}
          </button>
        )
      )}
    </div>
  );
};

function App() {
  const [isDark, setIsDark] = useTheme();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [socialStatus, setSocialStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [scrapeProgress, setScrapeProgress] = useState({ current: 0, total: 0, status: '' });
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const honeypotRef = useRef(null); // Anti-bot honeypot field
  const emailInputRef = useRef(null);
  const [sortBy, setSortBy] = useState("excitement");
  const [timeRange, setTimeRange] = useState("week");
  const getInitialPageView = () => {
    const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
    if (path === '/snaps' || path === '/snap') return 'snaps';
    if (path === '/comics' || path === '/comic' || path === '/toons') return 'comics';
    if (path === '/briefs' || path === '/brief') return 'briefs';
    if (path === '/toollab' || path === '/tools') return 'toollab';
    if (path === '/editorials' || path === '/editorial') return 'editorials';
    if (path === '/pulse') return 'pulse';
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    if (viewParam && ['snaps', 'comics', 'briefs', 'toollab', 'editorials', 'pulse', 'news'].includes(viewParam)) {
      return viewParam;
    }
    return 'news';
  };
  const [pageView, setPageView] = useState(getInitialPageView);

  const handleNavigateView = (view) => {
    setPageView(view);
    const targetPath = view === 'news' ? '/' : (view === 'comics' ? '/toons' : `/${view}`);
    if (window.location.pathname !== targetPath) {
      window.history.pushState({ view }, '', targetPath);
    }
  };

  useEffect(() => {
    const onPopState = () => {
      setPageView(getInitialPageView());
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const [editorials, setEditorials] = useState([]);
  const [editorialsLoading, setEditorialsLoading] = useState(false);
  const [selectedEditorial, setSelectedEditorial] = useState(null);
  const [pulsePosts, setPulsePosts] = useState([]);
  const [pulseLoading, setPulseLoading] = useState(false);
  const [linkedinPosts, setLinkedinPosts] = useState([]);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [toolGuides, setToolGuides] = useState([]);
  const [toolGuidesLoading, setToolGuidesLoading] = useState(false);
  const [selectedToolGuide, setSelectedToolGuide] = useState(null);
  const [modalSubEmail, setModalSubEmail] = useState('');
  const [modalSubscribing, setModalSubscribing] = useState(false);
  const [showCreateGuide, setShowCreateGuide] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [comics, setComics] = useState([]);
  const [comicsLoading, setComicsLoading] = useState(false);
  const [selectedComic, setSelectedComic] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [snapsLoading, setSnapsLoading] = useState(false);
  const [selectedSnap, setSelectedSnap] = useState(null);

  // Admin mode - activated by URL param ?admin=neural2026 or stored in localStorage
  const [isAdmin] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const adminKey = urlParams.get('admin');
    if (adminKey === 'neural2026') {
      localStorage.setItem('isAdmin', 'true');
      return true;
    }
    return localStorage.getItem('isAdmin') === 'true';
  });

  useEffect(() => {
    initAttribution();
  }, []);

  const fetchArticles = useCallback(async (tab = "all", sort = sortBy, range = timeRange) => {
    try {
      const response = await axios.get(`${API}/articles?limit=50&tab=${tab}&sort_by=${sort}&time_range=${range}`);
      setArticles(response.data.articles || []);
    } catch (e) {
      console.error('Error fetching articles:', e);
      toast.error('Failed to load articles');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    }
  }, []);

  const fetchSocialStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/social-post/status`);
      setSocialStatus(response.data);
    } catch (e) {
      console.error('Error fetching social status:', e);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (e) {
      console.error('Error fetching categories:', e);
    }
  }, []);

  const fetchEditorials = useCallback(async () => {
    setEditorialsLoading(true);
    try {
      const response = await axios.get(`${API}/editorials?limit=20`);
      setEditorials(response.data.editorials || []);
    } catch (e) {
      console.error('Error fetching editorials:', e);
    } finally {
      setEditorialsLoading(false);
    }
  }, []);

  const fetchPulsePosts = useCallback(async () => {
    setPulseLoading(true);
    try {
      const response = await axios.get(`${API}/social-posts?platform=pulse&limit=30`);
      setPulsePosts(response.data.posts || []);
    } catch (e) {
      console.error('Error fetching pulse posts:', e);
    } finally {
      setPulseLoading(false);
    }
  }, []);

  const fetchLinkedinPosts = useCallback(async () => {
    setLinkedinLoading(true);
    try {
      const response = await axios.get(`${API}/social-posts?platform=linkedin&limit=20`);
      setLinkedinPosts(response.data.posts || []);
    } catch (e) {
      console.error('Error fetching LinkedIn posts:', e);
    } finally {
      setLinkedinLoading(false);
    }
  }, []);

  const fetchToolGuides = useCallback(async () => {
    setToolGuidesLoading(true);
    try {
      const response = await axios.get(`${API}/tool-guides?limit=20`);
      setToolGuides(response.data.guides || []);
    } catch (e) {
      console.error('Error fetching tool guides:', e);
    } finally {
      setToolGuidesLoading(false);
    }
  }, []);

  const fetchComics = useCallback(async () => {
    setComicsLoading(true);
    try {
      const response = await axios.get(`${API}/comics?limit=30`);
      setComics(response.data.comics || []);
    } catch (e) {
      console.error('Error fetching comics:', e);
    } finally {
      setComicsLoading(false);
    }
  }, []);

  const fetchSnaps = useCallback(async () => {
    setSnapsLoading(true);
    try {
      const response = await axios.get(`${API}/snaps?limit=100`);
      setSnaps(response.data.snaps || []);
    } catch (e) {
      console.error('Error fetching snaps:', e);
    } finally {
      setSnapsLoading(false);
    }
  }, []);

  const handleReadStoryForSnap = async (snap) => {
    if (snap.editorial_id) {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/editorials/${snap.editorial_id}`);
        if (res.data) {
          setSelectedSnap(null);
          setSelectedEditorial(res.data);
          window.history.pushState({}, '', `?editorial=${snap.editorial_id}`);
          return;
        }
      } catch (err) {
        console.error('Failed to load editorial for snap:', err);
      } finally {
        setLoading(false);
      }
    }
    if (snap.article_id) {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/articles/${snap.article_id}`);
        if (res.data) {
          setSelectedSnap(null);
          setSelectedArticle(res.data);
          window.history.pushState({}, '', `?article=${snap.article_id}`);
          return;
        }
      } catch (err) {
        console.error('Failed to load article for snap:', err);
      } finally {
        setLoading(false);
      }
    }

    const isGenericHome = (url) => {
      if (!url || typeof url !== 'string') return true;
      try {
        const u = new URL(url);
        return u.pathname === '/' || u.pathname === '' || u.pathname === '/snaps' || u.pathname === '/snaps/';
      } catch {
        return url === 'https://theneuralfeed.com' || url === 'https://theneuralfeed.com/';
      }
    };

    if (snap.article_url && !isGenericHome(snap.article_url)) {
      window.open(snap.article_url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (snap.sources && snap.sources.length > 0) {
      const srcUrl = snap.sources[0]?.url || (typeof snap.sources[0] === 'string' ? snap.sources[0] : null);
      if (srcUrl && !isGenericHome(srcUrl)) {
        window.open(srcUrl, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    if (snap.source_urls && snap.source_urls.length > 0) {
      const src = snap.source_urls[0];
      if (src && !isGenericHome(src)) {
        window.open(src, '_blank', 'noopener,noreferrer');
        return;
      }
    }
  };

  const handleReadArticleForComic = async (articleId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/articles/${articleId}`);
      if (response.data) {
        setSelectedArticle(response.data);
      }
    } catch (e) {
      toast.error('Failed to load article detail');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchArticles(activeTab),
      fetchStats(),
      fetchSocialStatus(),
      fetchCategories(),
      fetchToolGuides(),
      fetchComics(),
      fetchSnaps()
    ]);
    setLoading(false);
  }, [fetchArticles, fetchStats, fetchCategories, fetchToolGuides, fetchComics, fetchSnaps, activeTab]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (pageView === 'editorials' && editorials.length === 0) {
      fetchEditorials();
    }
  }, [pageView, fetchEditorials, editorials.length]);

  useEffect(() => {
    if (pageView === 'snaps' && snaps.length === 0) {
      fetchSnaps();
    }
  }, [pageView, fetchSnaps, snaps.length]);

  useEffect(() => {
    const handleSnapDeepLink = async () => {
      const params = new URLSearchParams(window.location.search);
      const snapId = params.get('snap');
      if (snapId) {
        try {
          const response = await axios.get(`${API}/snaps/${snapId}`);
          if (response.data) {
            setSelectedSnap(response.data);
            setPageView('snaps');
          }
        } catch (e) {
          console.error('Error fetching snap for deep link:', e);
        }
      }
    };
    handleSnapDeepLink();
  }, []);

  useEffect(() => {
    if (pageView === 'pulse' && pulsePosts.length === 0) {
      fetchPulsePosts();
    }
  }, [pageView, fetchPulsePosts, pulsePosts.length]);

  useEffect(() => {
    if (pageView === 'briefs' && linkedinPosts.length === 0) {
      fetchLinkedinPosts();
    }
  }, [pageView, fetchLinkedinPosts, linkedinPosts.length]);

  useEffect(() => {
    if (pageView === 'toollab' && toolGuides.length === 0) {
      fetchToolGuides();
    }
  }, [pageView, fetchToolGuides, toolGuides.length]);

  useEffect(() => {
    if (pageView === 'comics' && comics.length === 0) {
      fetchComics();
    }
  }, [pageView, fetchComics, comics.length]);

  // Deep Link Handling - Fetch snap directly from API when ?snap= param exists
  useEffect(() => {
    const handleSnapDeepLink = async () => {
      const params = new URLSearchParams(window.location.search);
      const snapId = params.get('snap');
      if (snapId) {
        try {
          const response = await axios.get(`${API}/snaps/${snapId}`);
          if (response.data) {
            setSelectedSnap(response.data);
          }
        } catch (e) {
          console.error('Error fetching snap for deep link:', e);
        }
      }
    };
    handleSnapDeepLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle direct visits to /subscribe or #subscribe-section
  useEffect(() => {
    if (window.location.pathname === '/subscribe' || window.location.hash === '#subscribe-section') {
      const timer = setTimeout(() => {
        const el = document.getElementById('subscribe-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          emailInputRef.current?.focus();
        }
      }, 350);
      return () => clearTimeout(timer);
    }
  }, []);

  const scrollToSubscribe = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const el = document.getElementById('subscribe-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 400);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {
      toast.error('Failed to copy');
    }
  };

  // Handle email confirmation from URL
  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const isConfirmPage = window.location.pathname === '/confirm' || window.location.pathname.includes('confirm');

      if (token && isConfirmPage) {
        try {
          const response = await axios.get(`${API}/verify-email/${token}`);
          if (response.data.already_verified) {
            toast.info('Your email is already verified! You\'re all set.');
          } else {
            toast.success('Email verified! 🎉 You\'ll start receiving newsletters soon.');
          }
          // Clean up URL
          window.history.replaceState({}, document.title, '/');
        } catch (error) {
          console.error('Verification failed:', error);
          toast.error(error.response?.data?.detail || 'Verification failed. Please try subscribing again.');
          window.history.replaceState({}, document.title, '/');
        }
      }
    };
    handleEmailConfirmation();
  }, []);

  // Deep Link Handling - Fetch editorial directly from API when ?editorial= param exists
  useEffect(() => {
    const handleDeepLink = async () => {
      const params = new URLSearchParams(window.location.search);
      const editorialId = params.get('editorial');
      if (editorialId) {
        try {
          const response = await axios.get(`${API}/editorials/${editorialId}`);
          if (response.data) {
            setSelectedEditorial(response.data);
          }
        } catch (e) {
          console.error('Error fetching editorial for deep link:', e);
          // Editorial not found or error - just continue normally
        }
      }
    };
    handleDeepLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep Link Handling - Fetch brief directly from API when ?brief= param exists
  useEffect(() => {
    const handleBriefDeepLink = async () => {
      const params = new URLSearchParams(window.location.search);
      const briefId = params.get('brief');
      if (briefId) {
        try {
          // Fetch the brief directly by ID
          const response = await axios.get(`${API}/social-posts/${briefId}`);
          if (response.data) {
            setSelectedBrief(response.data);
            // Show on correct page based on platform
            if (response.data.platform === 'pulse') {
              setPageView('pulse');
            } else {
              setPageView('briefs');
            }
          }
        } catch (e) {
          console.error('Error fetching brief for deep link:', e);
        }
      }
    };
    handleBriefDeepLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep Link Handling - Fetch tool guide directly from API when ?toolguide= param exists
  useEffect(() => {
    const handleToolGuideDeepLink = async () => {
      const params = new URLSearchParams(window.location.search);
      const guideId = params.get('toolguide');
      if (guideId) {
        try {
          const response = await axios.get(`${API}/tool-guides/${guideId}`);
          if (response.data) {
            setSelectedToolGuide(response.data);
            setPageView('toollab');
          }
        } catch (e) {
          console.error('Error fetching tool guide for deep link:', e);
        }
      }
    };
    handleToolGuideDeepLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep Link Handling - Fetch comic directly from API when ?comic= param exists
  useEffect(() => {
    const handleComicDeepLink = async () => {
      const params = new URLSearchParams(window.location.search);
      const comicId = params.get('comic');
      if (comicId) {
        try {
          const response = await axios.get(`${API}/comics/${comicId}`);
          if (response.data) {
            setSelectedComic(response.data);
            setPageView('comics');
          }
        } catch (e) {
          console.error('Error fetching comic for deep link:', e);
        }
      }
    };
    handleComicDeepLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch articles when activeTab, sortBy, or timeRange changes
  useEffect(() => {
    fetchArticles(activeTab, sortBy, timeRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sortBy, timeRange]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  const handleScrape = async () => {
    setScraping(true);
    setScrapeProgress({ current: 0, total: stats?.total_sources || 172, status: 'Starting scrape...' });
    try {
      await axios.post(`${API}/scrape`);
      toast.success('Scraping started! Fetching full articles and generating AI summaries...');

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const status = await axios.get(`${API}/scrape/status`);

          // Update progress
          const articlesProcessed = status.data.articles_scraped || 0;
          const articlesWithSummaries = status.data.articles_summarized || 0;
          setScrapeProgress({
            current: articlesWithSummaries,
            total: articlesProcessed || stats?.total_sources || 172,
            status: `Processing: ${articlesWithSummaries} articles with AI summaries (${articlesProcessed} scraped)`
          });

          if (status.data.status === 'completed') {
            clearInterval(pollInterval);
            setScraping(false);
            setScrapeProgress({ current: 0, total: 0, status: '' });
            toast.success(`Done! ${status.data.articles_scraped} articles with AI insights ready.`);
            loadData();
          } else if (status.data.status === 'failed') {
            clearInterval(pollInterval);
            setScraping(false);
            setScrapeProgress({ current: 0, total: 0, status: '' });
            toast.error('Scraping failed. Please try again.');
          }
        } catch (e) {
          console.error('Error polling status:', e);
        }
      }, 3000); // Poll every 3 seconds for more frequent updates

      // Timeout after 10 minutes (longer for full article fetching)
      setTimeout(() => {
        clearInterval(pollInterval);
        setScraping(false);
        setScrapeProgress({ current: 0, total: 0, status: '' });
        loadData();
      }, 600000);
    } catch (e) {
      setScraping(false);
      toast.error('Failed to start scraping');
    }
  };

  const handleSendNewsletter = async () => {
    setSendingNewsletter(true);
    try {
      const response = await axios.post(`${API}/newsletter/send`);
      toast.success(response.data.message);
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to send newsletter';
      toast.error(errorMsg);
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleSubscribe = async (e, customEmail) => {
    if (e && e.preventDefault) e.preventDefault();
    const emailToUse = (customEmail || subscriberEmail || '').trim();
    if (!emailToUse) return;

    // Honeypot check - bots fill this, humans don't
    const honeypotValue = honeypotRef.current?.value;

    setSubscribing(true);
    try {
      const attr = getAttributionPayload('/');
      const utmSource = attr.utm_source || 'direct';
      const utmMedium = attr.utm_medium || 'web';
      const utmCampaign = attr.utm_campaign || 'none';

      await axios.post(`${API}/subscribers`, { 
        email: emailToUse,
        website: honeypotValue || "",
        ...attr
      });
      toast.success('Almost there! Check your email to confirm your subscription.', {
        description: 'We sent you a confirmation link. Click it to start getting The Neural Feed.'
      });

      // Fire signup event to Google Analytics
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'signup', {
          method: 'homepage_banner',
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign
        });
      }
      // Fire signup event to PostHog
      if (typeof window !== 'undefined' && window.posthog) {
        window.posthog.capture('signup', {
          method: 'homepage_banner',
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign
        });
      }

      setSubscriberEmail('');
      fetchStats();
      fetchSocialStatus();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || 'Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const handleModalSubscribe = async (e) => {
    e.preventDefault();
    if (!modalSubEmail || !modalSubEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setModalSubscribing(true);
    try {
      const curPath = typeof window !== 'undefined' ? window.location.pathname : '/';
      const attr = getAttributionPayload(curPath);
      const utmSource = attr.utm_source || 'direct';
      const utmMedium = attr.utm_medium || 'web';
      const utmCampaign = attr.utm_campaign || 'none';

      await axios.post(`${API}/subscribers`, { 
        email: modalSubEmail.trim(), 
        website: "",
        ...attr
      });
      toast.success('Almost there! Check your email to confirm your subscription.');

      // Fire signup event to Google Analytics
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'signup', {
          method: 'card_modal',
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign
        });
      }
      // Fire signup event to PostHog
      if (typeof window !== 'undefined' && window.posthog) {
        window.posthog.capture('signup', {
          method: 'card_modal',
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign
        });
      }

      setModalSubEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subscription failed. Please try again.');
    } finally {
      setModalSubscribing(false);
    }
  };

  const openArticle = (article) => {
    setSelectedArticle(article);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-container">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/logo192.png"
                alt="TNF"
                className="w-9 h-9 rounded-md object-cover border border-emerald-500/25 shadow-sm"
              />
              <div>
                <h1 className="text-lg font-black tracking-tighter">THE NEURAL FEED</h1>
                <p className="text-[10px] font-mono text-muted-foreground tracking-wider hidden sm:block">AI INTELLIGENCE • DAILY INSIGHTS</p>
              </div>
            </div>
            {/* Page Nav */}
            <div className="hidden sm:flex items-center bg-muted/50 rounded-full p-1 gap-0.5">
              <button
                onClick={() => handleNavigateView('news')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${pageView === 'news' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              >
                <Newspaper className="w-3 h-3" /> News
              </button>
              <button
                onClick={() => handleNavigateView('snaps')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${pageView === 'snaps' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              >
                <Zap className="w-3 h-3 text-emerald-400 fill-emerald-400" /> Neural Snap
              </button>
              <button
                onClick={() => handleNavigateView('briefs')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${pageView === 'briefs' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              >
                <FileText className="w-3 h-3" /> Briefs
              </button>
              <button
                onClick={() => handleNavigateView('toollab')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${pageView === 'toollab' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              >
                <Wrench className="w-3 h-3" /> Tool Lab
              </button>
              <button
                onClick={() => handleNavigateView('editorials')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${pageView === 'editorials' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              >
                <PenLine className="w-3 h-3" /> Editorials
              </button>
              <button
                onClick={() => handleNavigateView('comics')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${pageView === 'comics' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              >
                <Smile className="w-3 h-3" /> Neural Toons
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              data-testid="theme-toggle-btn"
              variant="ghost"
              size="icon"
              onClick={() => setIsDark(!isDark)}
              className="rounded-full h-8 w-8"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            <Link
              to="/issues"
              className="rounded-full px-3 py-1.5 text-xs border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hidden sm:inline-flex items-center gap-1.5 transition-colors font-medium mr-1"
            >
              <Sparkles className="w-3 h-3 text-amber-400" /> Past Issues
            </Link>

            <Button
              data-testid="header-subscribe-btn"
              size="sm"
              onClick={scrollToSubscribe}
              className="rounded-full px-3 sm:px-4 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white text-xs sm:text-sm cursor-pointer"
            >
              <Mail className="w-3 h-3 mr-1" />
              Subscribe
            </Button>

            {/* Admin-only buttons */}
            {isAdmin && (
              <>
                <Button
                  data-testid="scrape-btn"
                  onClick={handleScrape}
                  disabled={scraping}
                  size="sm"
                  className="rounded-full px-4 hidden sm:flex"
                >
                  {scraping ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      Scraping...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1.5" />
                      Scrape
                    </>
                  )}
                </Button>

                <Button
                  data-testid="send-newsletter-btn"
                  onClick={handleSendNewsletter}
                  disabled={sendingNewsletter || articles.length === 0}
                  variant="secondary"
                  size="sm"
                  className="rounded-full px-4 hidden sm:flex"
                >
                  {sendingNewsletter ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-3 h-3 mr-1.5" />
                      Newsletter
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Page Nav */}
      <div className="sm:hidden flex items-center justify-center bg-muted/30 border-b border-border px-4 py-2 overflow-x-auto">
        <div className="flex items-center bg-muted/50 rounded-full p-1 gap-0.5">
          <button
            onClick={() => handleNavigateView('news')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 whitespace-nowrap ${pageView === 'news' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
          >
            <Newspaper className="w-3 h-3" /> News
          </button>
          <button
            onClick={() => handleNavigateView('snaps')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 whitespace-nowrap ${pageView === 'snaps' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
          >
            <Zap className="w-3 h-3 text-emerald-400 fill-emerald-400" /> Neural Snap
          </button>
          <button
            onClick={() => handleNavigateView('briefs')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 whitespace-nowrap ${pageView === 'briefs' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
          >
            <FileText className="w-3 h-3" /> Briefs
          </button>
          <button
            onClick={() => handleNavigateView('toollab')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 whitespace-nowrap ${pageView === 'toollab' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
          >
            <Wrench className="w-3 h-3" /> Tool Lab
          </button>
          <button
            onClick={() => handleNavigateView('editorials')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 whitespace-nowrap ${pageView === 'editorials' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
          >
            <PenLine className="w-3 h-3" /> Editorials
          </button>
          <button
            onClick={() => handleNavigateView('comics')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 whitespace-nowrap ${pageView === 'comics' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
          >
            <Smile className="w-3 h-3" /> Neural Toons
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Section */}
        <section className={`grid grid-cols-2 ${(stats?.total_subscribers || 0) > 1000 ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3 mb-6`}>
          <StatsCard
            icon={FileText}
            label="Articles"
            value={loading ? '...' : `${stats?.articles_this_week || stats?.total_articles || 0} this week`}
          />
          {(stats?.total_subscribers || 0) > 1000 && (
            <StatsCard
              icon={Users}
              label="Subscribers"
              value={loading ? '...' : (stats?.total_subscribers || 0)}
            />
          )}
          <StatsCard
            icon={TrendingUp}
            label="Sources"
            value={loading ? '...' : `${stats?.total_sources || 92}+`}
          />
          <StatsCard
            icon={Clock}
            label="Updated"
            value={stats?.last_scrape?.completed_at ?
              format(new Date(stats.last_scrape.completed_at), 'MMM d, h:mm a') :
              (stats?.last_scrape?.status === 'running' ? 'In Progress' : 'Never')}
          />
        </section>

        {/* Social Auto-Posting Admin Panel */}
        {isAdmin && (
          <section className="mb-6">
            <Card className="border border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-primary" />
                    Social Auto-Posting
                  </h3>
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/social-post/status`);
                        setSocialStatus(res.data);
                      } catch (e) {
                        toast.error('Failed to fetch social status');
                      }
                    }}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    Refresh Status
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Bluesky */}
                  <div className={`p-3 rounded-lg border ${socialStatus?.bluesky?.configured ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/50 border-border'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2 h-2 rounded-full ${socialStatus?.bluesky?.configured ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <span className="text-xs font-semibold">Bluesky</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mb-2">
                      {socialStatus?.bluesky?.configured ? `✅ ${socialStatus.bluesky.handle}` : '❌ Not configured'}
                    </p>
                    {socialStatus?.bluesky?.configured && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.post(`${API}/social-post/test-bluesky`);
                            if (res.data.success) {
                              toast.success(`Posted! ${res.data.post_url}`);
                            } else {
                              toast.error(res.data.error || 'Post failed');
                            }
                          } catch (e) {
                            toast.error('Bluesky test post failed');
                          }
                        }}
                        className="text-xs bg-primary text-primary-foreground px-3 py-1 rounded-full hover:bg-primary/90 transition-colors"
                      >
                        Test Post
                      </button>
                    )}
                  </div>
                  {/* Twitter/X */}
                  <div className={`p-3 rounded-lg border ${socialStatus?.twitter?.configured ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/50 border-border'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2 h-2 rounded-full ${socialStatus?.twitter?.configured ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <span className="text-xs font-semibold">Twitter/X</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {socialStatus?.twitter?.configured ? `✅ ${socialStatus.twitter.username}` : '❌ Not configured (Playwright)'}
                    </p>
                  </div>
                  {/* LinkedIn */}
                  <div className={`p-3 rounded-lg border ${socialStatus?.linkedin?.configured ? 'bg-green-500/5 border-green-500/20' : 'bg-muted/50 border-border'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2 h-2 rounded-full ${socialStatus?.linkedin?.configured ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <span className="text-xs font-semibold">LinkedIn</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {socialStatus?.linkedin?.configured ? `✅ ${socialStatus.linkedin.username}` : '❌ Not configured (Playwright)'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Subscribe Section */}
        <section id="subscribe-section" className="mb-6">
          <Card className="border-2 border-primary/20 bg-gradient-to-r from-blue-500/10 via-violet-500/10 to-accent/5 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-xl font-bold mb-1">📬 The week's biggest AI news, free every Monday</h2>
                  <p className="text-sm text-muted-foreground mb-2">The Neural Feed Weekly: 6–8 AI stories in a 5-minute read, plus a page of AI-made comics at the back.</p>
                  <div className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="text-green-500">✓</span> Free</span>
                    <span className="flex items-center gap-1"><span className="text-green-500">✓</span> One email every Monday</span>
                    <span className="flex items-center gap-1"><span className="text-green-500">✓</span> Unsubscribe anytime</span>
                  </div>
                </div>
                <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto">
                  {/* Honeypot field - invisible to humans, traps bots */}
                  <input
                    ref={honeypotRef}
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      width: '1px',
                      height: '1px',
                      opacity: 0
                    }}
                  />
                  <Input
                    ref={emailInputRef}
                    data-testid="subscribe-email-input"
                    type="email"
                    placeholder="Your email address"
                    value={subscriberEmail}
                    onChange={(e) => setSubscriberEmail(e.target.value)}
                    className="w-full md:w-56 rounded-full h-10 text-sm"
                  />
                  <Button
                    data-testid="subscribe-btn"
                    type="submit"
                    disabled={subscribing || !subscriberEmail}
                    size="sm"
                    className="rounded-full px-5 h-10 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-medium"
                  >
                    {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get Monday's issue <ArrowRight className="w-3 h-3 ml-1" /></>}
                  </Button>
                </form>
              </div>

            </CardContent>
          </Card>
        </section>

        {/* Only show news-specific UI when on news view */}
        {pageView === 'news' && (
          <>
            {/* Category Tabs */}
            <section className="mb-6">
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="w-full h-auto flex-wrap justify-start gap-1 bg-transparent p-0" data-testid="category-tabs">
                  {categories.map((cat) => (
                    <TabsTrigger
                      key={cat.id}
                      value={cat.id}
                      data-testid={`tab-${cat.id}`}
                      className="rounded-full px-3 py-1.5 text-xs font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    >
                      {cat.name}
                      <span className="ml-1.5 text-[10px] opacity-70">({cat.count})</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </section>

            {/* Scrape Status Banner - Admin only */}
            {isAdmin && stats?.last_scrape && (
              <div className={`mb-4 p-3 rounded-md flex items-center gap-2 text-sm ${stats.last_scrape.status === 'completed'
                ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                : stats.last_scrape.status === 'running'
                  ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                  : 'bg-red-500/10 text-red-600 dark:text-red-400'
                }`}>
                {stats.last_scrape.status === 'completed' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : stats.last_scrape.status === 'running' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                <span className="font-medium">
                  {stats.last_scrape.status === 'completed'
                    ? `${stats.last_scrape.articles_scraped} articles with AI insights • ${stats.last_scrape.articles_filtered || 0} boring filtered`
                    : stats.last_scrape.status === 'running'
                      ? 'Fetching articles & generating AI summaries...'
                      : 'Last scrape failed'}
                </span>
              </div>
            )}

            {/* Progress Bar - Show during scraping - Admin only */}
            {isAdmin && scraping && scrapeProgress.total > 0 && (
              <Card className="mb-4 border-yellow-500/20 bg-yellow-500/5">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-yellow-600 dark:text-yellow-400">
                        {scrapeProgress.status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {scrapeProgress.current} / {scrapeProgress.total}
                      </span>
                    </div>
                    <Progress
                      value={(scrapeProgress.current / scrapeProgress.total) * 100}
                      className="h-2"
                    />
                    <p className="text-xs text-muted-foreground">
                      Scraping {stats?.total_sources || 172} sources, fetching full articles, and generating AI summaries with DeepSeek...
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Articles Grid */}
            <section>
              {/* Header with filters */}
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold tracking-tight">
                    {activeTab === 'all' ? 'Latest AI News' : categories.find(c => c.id === activeTab)?.name || 'News'}
                  </h2>
                  <span className="text-xs font-mono text-muted-foreground">
                    {articles.length} articles
                  </span>
                </div>

                {/* Filter Controls */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Time Range Filter */}
                  <div className="flex items-center gap-1 bg-muted/50 rounded-full p-1">
                    {[
                      { id: 'today', label: 'Today' },
                      { id: 'week', label: 'This Week' },
                      { id: 'month', label: 'Month' },
                      { id: 'all', label: 'All Time' }
                    ].map((range) => (
                      <button
                        key={range.id}
                        onClick={() => handleTimeRangeChange(range.id)}
                        className={`px-3 py-1 text-xs rounded-full transition-all ${timeRange === range.id
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted text-muted-foreground'
                          }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>

                  {/* Sort Dropdown */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="appearance-none bg-muted/50 text-sm px-3 py-1.5 pr-8 rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-primary"
                    >
                      <option value="excitement">Most Exciting</option>
                      <option value="recent">Latest</option>
                      <option value="authority">Top Sources</option>
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i} className="border border-border overflow-hidden">
                      <div className="bg-muted/30 px-5 py-3">
                        <Skeleton className="h-4 w-full" />
                      </div>
                      <CardContent className="p-5">
                        <Skeleton className="h-4 w-20 mb-3" />
                        <Skeleton className="h-5 w-full mb-2" />
                        <Skeleton className="h-5 w-3/4 mb-3" />
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-2/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : articles.length === 0 ? (
                <Card className="border-2 border-dashed border-border">
                  <CardContent className="p-8 text-center">
                    <Newspaper className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <h3 className="text-lg font-bold mb-1">No articles in this category</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {isAdmin
                        ? (activeTab === 'all' ? 'Click "Scrape" to fetch AI news with insights' : 'Try another category or scrape for new articles')
                        : 'Check back soon for the latest AI news!'}
                    </p>
                    {isAdmin && (
                      <Button
                        onClick={handleScrape}
                        disabled={scraping}
                        className="rounded-full px-6"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Start Scraping
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeTab === 'all' && snaps.slice(0, 1).map((snap) => (
                    <SnapCard
                      key={`home-snap-${snap.id}`}
                      snap={snap}
                      onClick={(s) => {
                        setSelectedSnap(s);
                        window.history.pushState({}, '', `?snap=${s.id}`);
                      }}
                      onReadStoryClick={handleReadStoryForSnap}
                    />
                  ))}
                  {activeTab === 'all' && comics.slice(0, 2).map((comic) => (
                    <ComicCard
                      key={`home-comic-${comic.short_id || comic.id}`}
                      comic={comic}
                      onClick={(c) => {
                        setSelectedComic(c);
                        window.history.pushState({}, '', `?comic=${c.short_id}`);
                      }}
                      onReadArticleClick={handleReadArticleForComic}
                    />
                  ))}
                  {articles.map((article) => {
                    const relatedGuide = toolGuides.find(g => g.source_article_id === article.id);
                    return (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        onClick={openArticle}
                        relatedGuide={relatedGuide}
                        onToolGuideClick={(guide) => {
                          setSelectedArticle(null);
                          setSelectedToolGuide(guide);
                          window.history.pushState({}, '', `?toolguide=${guide.id}`);
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
        {/* ====== NEURAL SNAP VIEW (Visual 4:5 News Cards) ====== */}
        {pageView === 'snaps' && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-400 fill-emerald-400" />
                  Neural Snap
                </h2>
                <p className="text-xs text-muted-foreground mt-1">High-impact visual news cards breaking down the biggest AI stories in one snap</p>
              </div>
              {isAdmin && (
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/snaps/generate?force=true`);
                      toast.success('Neural Snap generation started! Will be ready in ~30s');
                      setTimeout(() => fetchSnaps(), 30000);
                    } catch (e) {
                      toast.error('Failed to trigger snap generation');
                    }
                  }}
                  size="sm"
                  variant="outline"
                  className="rounded-full border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                >
                  <Sparkles className="w-3 h-3 mr-1.5" /> Generate Snap
                </Button>
              )}
            </div>

            {snapsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="border border-border overflow-hidden bg-card/65 backdrop-blur-md">
                    <Skeleton className="aspect-[4/5] w-full" />
                    <CardContent className="p-4 flex flex-col gap-3">
                      <Skeleton className="h-4 w-3/4 animate-pulse" />
                      <Skeleton className="h-3 w-1/2 animate-pulse" />
                      <Skeleton className="h-8 w-full mt-2 animate-pulse" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : snaps.length === 0 ? (
              <Card className="border-2 border-dashed border-border max-w-2xl mx-auto bg-card/65 backdrop-blur-md">
                <CardContent className="p-8 text-center">
                  <Zap className="w-12 h-12 mx-auto text-emerald-400/50 mb-3" />
                  <h3 className="text-lg font-bold mb-1">No Neural Snaps yet</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Neural Snaps are generated daily from the most gripping AI breaking stories.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto animate-in fade-in duration-500">
                {snaps.map((snap) => (
                  <SnapCard
                    key={snap.id}
                    snap={snap}
                    onClick={(s) => {
                      setSelectedSnap(s);
                      window.history.pushState({}, '', `?snap=${s.id}`);
                    }}
                    onReadStoryClick={handleReadStoryForSnap}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ====== BRIEFS VIEW ====== */}
        {pageView === 'briefs' && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Neural Briefs
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Analytical briefs on viral AI topics — ready to share</p>
              </div>
              {isAdmin && (
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/social-posts/generate`);
                      toast.success('Brief generation started!');
                      setTimeout(() => fetchLinkedinPosts(), 30000);
                    } catch (e) {
                      toast.error('Failed to trigger brief generation');
                    }
                  }}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                >
                  <Sparkles className="w-3 h-3 mr-1.5" /> Generate
                </Button>
              )}
            </div>

            {linkedinLoading ? (
              <div className="space-y-4 max-w-2xl mx-auto">
                {[...Array(2)].map((_, i) => (
                  <Card key={i} className="border border-border">
                    <CardContent className="p-5">
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-3/4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : linkedinPosts.length === 0 ? (
              <Card className="border-2 border-dashed border-border max-w-2xl mx-auto">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <h3 className="text-lg font-bold mb-1">No briefs yet</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Neural Briefs are generated daily from the most viral AI stories
                  </p>
                  {isAdmin && (
                    <Button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/social-posts/generate`);
                          toast.success('Brief generation started!');
                          setTimeout(() => fetchLinkedinPosts(), 30000);
                        } catch (e) {
                          toast.error('Failed to trigger generation');
                        }
                      }}
                      className="rounded-full px-6"
                    >
                      <Sparkles className="w-4 h-4 mr-2" /> Generate Now
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4 max-w-2xl mx-auto">
                {linkedinPosts.map((post) => (
                  <Card key={post.id} className="border border-border hover:border-primary/30 transition-all cursor-pointer"
                    onClick={() => setSelectedBrief(post)}>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <FileText className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold">The Neural Feed</p>
                          <p className="text-[10px] text-muted-foreground">{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</p>
                        </div>
                      </div>
                      <BriefPreview content={post.content} title={post.source_article_title} hook={post.hook} onReadMore={() => setSelectedBrief(post)} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}


        {/* Brief Detail Dialog */}
        <Dialog open={!!selectedBrief} onOpenChange={(open) => {
          if (!open) {
            setSelectedBrief(null);
            // Clear brief from URL so deep link effect doesn't re-trigger
            if (window.location.search.includes('brief=')) {
              window.history.replaceState({}, document.title, '/');
            }
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            {selectedBrief && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold">The Neural Feed</p>
                      <p className="text-[10px] text-muted-foreground">{formatDistanceToNow(new Date(selectedBrief.created_at), { addSuffix: true })}</p>
                    </div>
                  </div>
                  {selectedBrief.source_article_title && (
                    <p className="text-xs text-muted-foreground mb-1">Based on: {selectedBrief.source_article_title}</p>
                  )}
                </DialogHeader>
                <div className="mt-2">
                  <FormattedBrief
                    content={selectedBrief.content}
                    title={selectedBrief.source_article_title}
                    hook={selectedBrief.hook}
                    keyTakeaways={selectedBrief.key_takeaways}
                    closing={selectedBrief.closing}
                  />
                </div>

                {/* Subscribe CTA inside Brief Dialog */}
                <div className="mt-6 pt-4 border-t border-border/50 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/30 rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    <div className="flex-1 text-center sm:text-left">
                      <h4 className="font-bold text-sm text-amber-400">The week's biggest AI news, free every Monday</h4>
                      <p className="text-xs text-muted-foreground">Free every Monday. Unsubscribe anytime.</p>
                    </div>
                    <form
                      onSubmit={handleSubscribe}
                      className="flex gap-2 w-full sm:w-auto"
                    >
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={subscriberEmail}
                        onChange={(e) => setSubscriberEmail(e.target.value)}
                        className="w-full sm:w-48 rounded-full h-9 text-sm"
                        required
                      />
                      <Button
                        type="submit"
                        disabled={subscribing || !subscriberEmail}
                        size="sm"
                        className="rounded-full px-4 h-9 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white font-medium"
                      >
                        {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get Monday's issue →</>}
                      </Button>
                    </form>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-border">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2 mb-3">
                    <Share2 className="w-4 h-4" /> Share This Brief
                  </h4>
                  <SocialShareBar
                    url={`https://theneuralfeed.com/brief/${selectedBrief.id}`}
                    text={buildShareCaption({
                      type: 'brief',
                      title: selectedBrief.topic || selectedBrief.source_article_title,
                      summary: cleanForSocial(selectedBrief.hook || selectedBrief.content).substring(0, 280),
                      url: `https://theneuralfeed.com/brief/${selectedBrief.id}`,
                    })}
                    linkedinText={buildLinkedInCaption({
                      type: 'brief',
                      title: selectedBrief.topic || selectedBrief.source_article_title,
                      content: selectedBrief.content,
                      url: `https://theneuralfeed.com/brief/${selectedBrief.id}`,
                      hook: selectedBrief.hook,
                    })}
                  />
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ====== TOOL LAB VIEW ====== */}
        {pageView === 'toollab' && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-primary" />
                  Tool Lab
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Practical guides for AI tools — human tested, human written</p>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setShowCreateGuide(true)}
                    size="sm"
                    className="rounded-full"
                  >
                    <PenLine className="w-3 h-3 mr-1.5" /> Create Guide
                  </Button>
                </div>
              )}
            </div>

            {toolGuidesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="border border-border overflow-hidden">
                    <CardContent className="p-5">
                      <Skeleton className="h-4 w-20 mb-3" />
                      <Skeleton className="h-5 w-full mb-2" />
                      <Skeleton className="h-5 w-3/4 mb-3" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-2/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : toolGuides.length === 0 ? (
              <Card className="border-2 border-dashed border-border max-w-2xl mx-auto">
                <CardContent className="p-8 text-center">
                  <Wrench className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <h3 className="text-lg font-bold mb-1">No guides yet</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Tool Lab features a new AI tool every day with honest, practical getting-started guides
                  </p>
                  {isAdmin && (
                    <Button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/tool-guides/generate`);
                          toast.success('Tool guide generation started!');
                          setTimeout(() => fetchToolGuides(), 45000);
                        } catch (e) {
                          toast.error('Failed to trigger generation');
                        }
                      }}
                      className="rounded-full px-6"
                    >
                      <Sparkles className="w-4 h-4 mr-2" /> Generate Now
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {toolGuides.map((guide) => (
                  <Card
                    key={guide.id}
                    className="border border-border hover:border-primary/30 transition-all cursor-pointer overflow-hidden"
                    onClick={() => {
                      setSelectedToolGuide(guide);
                      window.history.pushState({}, '', `?toolguide=${guide.id}`);
                    }}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Wrench className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{guide.tool_name}</p>
                          <p className="text-[10px] text-muted-foreground">{guide.category} · {guide.difficulty}</p>
                        </div>
                      </div>
                      <h3 className="text-sm font-bold leading-tight mb-1 line-clamp-2">{guide.title}</h3>
                      {guide.tagline && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{guide.tagline}</p>
                      )}
                      {!guide.tagline && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{guide.overview}</p>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span>{guide.steps?.length || 0} steps</span>
                        <span>·</span>
                        <span>{guide.category}</span>
                        <span>·</span>
                        <span>{formatDistanceToNow(new Date(guide.created_at), { addSuffix: true })}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ====== CREATE GUIDE DIALOG ====== */}
        <Dialog open={showCreateGuide} onOpenChange={setShowCreateGuide}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Tool Guide</DialogTitle>
              <DialogDescription>Write a practical guide based on your real testing</DialogDescription>
            </DialogHeader>
            <CreateGuideForm
              onSubmit={async (guideData) => {
                try {
                  await axios.post(`${API}/tool-guides`, guideData);
                  toast.success('Tool guide published!');
                  setShowCreateGuide(false);
                  fetchToolGuides();
                } catch (e) {
                  toast.error('Failed to publish guide');
                }
              }}
              onCancel={() => setShowCreateGuide(false)}
            />
          </DialogContent>
        </Dialog>

        {/* ====== EDITORIALS VIEW ====== */}
        {pageView === 'editorials' && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                  <PenLine className="w-5 h-5 text-primary" />
                  Editorials
                </h2>
                <p className="text-xs text-muted-foreground mt-1">AI-drafted analysis of today's biggest stories, with links to the sources</p>
              </div>
              {isAdmin && (
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/editorials/generate`);
                      toast.success('Editorial generation started!');
                      setTimeout(() => fetchEditorials(), 30000);
                    } catch (e) {
                      toast.error('Failed to trigger editorial generation');
                    }
                  }}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                >
                  <Sparkles className="w-3 h-3 mr-1.5" /> Generate
                </Button>
              )}
            </div>

            {editorialsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="border border-border overflow-hidden">
                    <div className="bg-muted/30 px-5 py-3">
                      <Skeleton className="h-3 w-20" />
                    </div>
                    <CardContent className="p-5">
                      <Skeleton className="h-3 w-16 mb-3" />
                      <Skeleton className="h-5 w-full mb-2" />
                      <Skeleton className="h-5 w-3/4 mb-3" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-2/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : editorials.length === 0 ? (
              <Card className="border-2 border-dashed border-border">
                <CardContent className="p-8 text-center">
                  <PenLine className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <h3 className="text-lg font-bold mb-1">No editorials yet</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Editorials are generated daily from the most viral AI stories
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {editorials.map((editorial, index) => (
                  <EditorialCard
                    key={editorial.id}
                    editorial={editorial}
                    isBest={index === 0}
                    onClick={() => {
                      setSelectedEditorial(editorial);
                      window.history.pushState({}, '', `?editorial=${editorial.id}`);
                    }}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ====== NEURAL TOONS VIEW ====== */}
        {pageView === 'comics' && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                  <Palette className="w-5 h-5 text-primary" />
                  Neural Toons
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Vibrant AI-generated tech comic strips explaining the daily news</p>
              </div>
              {isAdmin && (
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/comics/generate`);
                      toast.success('Comic generation started!');
                      setTimeout(() => fetchComics(), 30000);
                    } catch (e) {
                      toast.error('Failed to trigger comic generation');
                    }
                  }}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                >
                  <Sparkles className="w-3 h-3 mr-1.5" /> Generate
                </Button>
              )}
            </div>

            {comicsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="border border-border overflow-hidden bg-card/65 backdrop-blur-md">
                    <Skeleton className="aspect-video w-full" />
                    <CardContent className="p-4 flex flex-col gap-3">
                      <Skeleton className="h-4 w-3/4 animate-pulse" />
                      <Skeleton className="h-3 w-1/2 animate-pulse" />
                      <Skeleton className="h-8 w-full mt-2 animate-pulse" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : comics.length === 0 ? (
              <Card className="border-2 border-dashed border-border bg-card/65 backdrop-blur-md">
                <CardContent className="p-8 text-center">
                  <Smile className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <h3 className="text-lg font-bold mb-1">No Neural Toons yet</h3>
                  <p className="text-sm text-muted-foreground">
                    New Neural Toons are on the way. Check back shortly!
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in duration-500">
                {comics.map((comic) => (
                  <ComicCard
                    key={comic.short_id || comic.id}
                    comic={comic}
                    onClick={(c) => {
                      setSelectedComic(c);
                      window.history.pushState({}, '', `?comic=${c.short_id}`);
                    }}
                    onReadArticleClick={handleReadArticleForComic}
                  />
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      {/* Article Detail Dialog */}
      <Dialog open={!!selectedArticle} onOpenChange={(open) => {
        if (!open) {
          setSelectedArticle(null);
          if (window.location.search.includes('article=')) {
            window.history.pushState({}, '', window.location.pathname);
          }
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="article-dialog">
          {selectedArticle && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {selectedArticle.priority_tier && (
                    <Badge className={`font-mono text-xs ${selectedArticle.priority_tier === 'VIRAL' ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white' :
                      selectedArticle.priority_tier === 'HOT' ? 'bg-orange-500 text-white' :
                        'bg-blue-500 text-white'
                      }`}>
                      {selectedArticle.priority_tier === 'VIRAL' && <Sparkles className="w-3 h-3 mr-1" />}
                      {selectedArticle.priority_tier}
                    </Badge>
                  )}
                  <Badge variant="outline" className="font-mono text-xs">
                    {selectedArticle.category}
                  </Badge>
                  <span className="text-xs font-mono text-muted-foreground">
                    {selectedArticle.source_name}
                  </span>
                </div>
                <DialogTitle className="text-xl leading-tight pr-8">
                  {selectedArticle.simplified_title || selectedArticle.title}
                </DialogTitle>
                <DialogDescription className="text-xs font-mono text-muted-foreground">
                  {selectedArticle.scraped_at && format(new Date(selectedArticle.scraped_at), 'MMMM d, yyyy')}
                </DialogDescription>
              </DialogHeader>

              {/* Hook */}
              {selectedArticle.hook && (
                <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-4 mt-2">
                  <div className="flex items-start gap-2">
                    <Zap className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                    <p className="text-base font-semibold">{selectedArticle.hook}</p>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="mt-4">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-2">
                  Key Insight
                </h4>
                <p className="text-base leading-relaxed">
                  {selectedArticle.summary || 'No summary available'}
                </p>
              </div>

              {/* Why it matters */}
              {selectedArticle.why_it_matters && (
                <div className="mt-4 bg-muted/50 rounded-lg p-4">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-1">
                    Why It Matters
                  </h4>
                  <p className="text-sm leading-relaxed">
                    {selectedArticle.why_it_matters}
                  </p>
                </div>
              )}

              {/* Key Points */}
              {selectedArticle.key_points && selectedArticle.key_points.length > 0 && (
                <div className="mt-4 bg-primary/5 rounded-lg p-4">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-2">
                    Key Points
                  </h4>
                  <ul className="space-y-2">
                    {selectedArticle.key_points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Related Tool Guide */}
              {(() => {
                const relatedGuide = toolGuides.find(g => g.source_article_id === selectedArticle.id);
                if (!relatedGuide) return null;
                return (
                  <div className="mt-4 bg-primary/5 rounded-lg p-4 border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Wrench className="w-4 h-4 text-primary" />
                      <h4 className="font-semibold text-sm uppercase tracking-wider text-primary">
                        Tool Lab Guide
                      </h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Deep-dive practical guide for {relatedGuide.tool_name}
                    </p>
                    <button
                      onClick={() => {
                        setSelectedArticle(null);
                        setSelectedToolGuide(relatedGuide);
                      }}
                      className="inline-flex items-center gap-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors px-4 py-2 rounded-full border border-primary/30 hover:border-primary/50 bg-primary/5"
                    >
                      <BookOpen className="w-3.5 h-3.5" />
                      Read the Guide
                    </button>
                  </div>
                );
              })()}

              <div className="mt-6 pt-4 border-t border-border space-y-3">
                {/* Deep Dive Link */}
                {selectedArticle.detailed_summary && (
                  <Button
                    variant="outline"
                    asChild
                    className="w-full rounded-full"
                  >
                    <a href={selectedArticle.share_url || `https://theneuralfeed.com/api/article/page?url=${encodeURIComponent(selectedArticle.url || '')}`} target="_blank" rel="noopener noreferrer">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Deep Dive
                    </a>
                  </Button>
                )}

                {/* Fallback Deep Dive - always show if article has URL */}
                {!selectedArticle.detailed_summary && selectedArticle.url && (
                  <Button
                    variant="outline"
                    asChild
                    className="w-full rounded-full"
                  >
                    <a href={selectedArticle.share_url || `https://theneuralfeed.com/api/article/page?url=${encodeURIComponent(selectedArticle.url)}`} target="_blank" rel="noopener noreferrer">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Deep Dive
                    </a>
                  </Button>
                )}

                {/* Subscribe CTA inside Article Dialog */}
                <div className="mt-4 pt-4 border-t border-border/50 bg-gradient-to-r from-primary/10 to-accent/5 rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    <div className="flex-1 text-center sm:text-left">
                      <h4 className="font-bold text-sm">🚀 Stay ahead in AI</h4>
                      <p className="text-xs text-muted-foreground">Understand AI in 5 minutes a day. No jargon. No spam. Unsubscribe anytime.</p>
                    </div>
                    <form
                      onSubmit={handleSubscribe}
                      className="flex gap-2 w-full sm:w-auto"
                    >
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={subscriberEmail}
                        onChange={(e) => setSubscriberEmail(e.target.value)}
                        className="w-full sm:w-48 rounded-full h-9 text-sm"
                        required
                      />
                      <Button
                        type="submit"
                        disabled={subscribing || !subscriberEmail}
                        size="sm"
                        className="rounded-full px-4 h-9 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white"
                      >
                        {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      </Button>
                    </form>
                  </div>
                </div>

                {/* Share Buttons */}
                {selectedArticle.id && (
                  <div className="mt-2">
                    <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2 mb-2">
                      <Share2 className="w-3 h-3" /> Share
                    </h4>
                    <SocialShareBar
                      url={selectedArticle.share_url || 'https://theneuralfeed.com'}
                      text={buildShareCaption({
                        type: 'article',
                        title: selectedArticle.title,
                        summary: selectedArticle.hook || selectedArticle.summary || '',
                        url: selectedArticle.share_url || 'https://theneuralfeed.com',
                      })}
                      imageUrl={`https://theneuralfeed.com/og/article-${selectedArticle.id}.png`}
                    />
                  </div>
                )}

                {/* News Magazine Signup Box inside Article Dialog */}
                <div className="my-4 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/30 text-left">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-bold text-xs uppercase tracking-wider text-amber-400 font-mono">📬 The week's biggest AI news, free every Monday</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                    The Neural Feed Weekly: 6–8 AI stories in a 5-minute read, plus a page of AI-made comics at the back.
                  </p>
                  <form onSubmit={handleModalSubscribe} className="flex gap-2">
                    <Input
                      type="email"
                      required
                      placeholder="you@example.com"
                      value={subscriberEmail}
                      onChange={(e) => setSubscriberEmail(e.target.value)}
                      className="h-9 text-xs bg-background/80 border-border"
                    />
                    <Button type="submit" size="sm" className="bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs shrink-0">
                      Get Monday's issue
                    </Button>
                  </form>
                  <p className="text-[10px] text-muted-foreground mt-2">Free. Unsubscribe anytime.</p>
                </div>

                {/* Original Article Link */}
                <Button
                  data-testid="read-original-btn"
                  asChild
                  className="w-full rounded-full"
                >
                  <a
                    href={
                      selectedArticle.url && selectedArticle.url.includes('viralwire.neuralfeed.ai')
                        ? `https://www.google.com/search?q=${encodeURIComponent(selectedArticle.title || 'AI News')}`
                        : selectedArticle.url && !selectedArticle.url.startsWith('http')
                          ? `https://${selectedArticle.url}`
                          : selectedArticle.url || `https://www.google.com/search?q=${encodeURIComponent(selectedArticle.title || 'AI News')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Read Original Article
                  </a>
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Editorial Detail Dialog */}
      <Dialog open={!!selectedEditorial} onOpenChange={(open) => {
        if (!open) {
          setSelectedEditorial(null);
          window.history.pushState({}, '', window.location.pathname);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="editorial-dialog">
          {selectedEditorial && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className="bg-primary text-primary-foreground font-mono text-[10px]">
                    <PenLine className="w-3 h-3 mr-1" /> EDITORIAL
                  </Badge>
                  <Badge variant="outline" className="font-mono text-xs">{selectedEditorial.category}</Badge>
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <BookOpen className="w-3 h-3" /> {selectedEditorial.reading_time_min} min read
                  </span>
                </div>
                <DialogTitle className="text-xl leading-tight pr-8">
                  {selectedEditorial.headline}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
                  {selectedEditorial.subheadline}
                </DialogDescription>
                {selectedEditorial.poster_url && (
                  <div className="mt-4 -mx-2 sm:mx-0">
                    <img
                      src={selectedEditorial.poster_url}
                      alt={selectedEditorial.headline}
                      className="w-full rounded-lg border border-border/60 shadow-sm"
                      loading="lazy"
                    />
                  </div>
                )}
              </DialogHeader>

              {/* Article Body */}
              <div className="mt-6 space-y-4">
                {(selectedEditorial.body || '')
                  .replace(/\\n/g, '\n')
                  .replace(/<br\s*\/?>/gi, '\n')
                  .split(/\n\n+|\n(?=[A-Z])/)
                  .filter(para => para.trim())
                  .map((para, i) => (
                    <p key={i} className="text-sm leading-relaxed text-foreground/90">
                      {para.trim()}
                    </p>
                  ))}
              </div>

              {/* Key Takeaways */}
              {selectedEditorial.key_takeaways?.length > 0 && (
                <div className="mt-6 bg-muted/50 rounded-lg p-4">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Key Takeaways</h4>
                  <ul className="space-y-2">
                    {selectedEditorial.key_takeaways.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-primary font-bold mt-0.5">→</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tags */}
              {selectedEditorial.tags?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedEditorial.tags.map((tag, i) => (
                    <span key={i} className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">#{tag}</span>
                  ))}
                </div>
              )}

              {/* Subscribe CTA inside Editorial Dialog */}
              <div className="mt-6 pt-4 border-t border-border/50 bg-gradient-to-r from-primary/10 to-accent/5 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div className="flex-1 text-center sm:text-left">
                    <h4 className="font-bold text-sm">🚀 Get more AI deep dives</h4>
                    <p className="text-xs text-muted-foreground">Understand AI in 5 minutes a day. No jargon. No spam. Unsubscribe anytime.</p>
                  </div>
                  <form
                    onSubmit={handleSubscribe}
                    className="flex gap-2 w-full sm:w-auto"
                  >
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={subscriberEmail}
                      onChange={(e) => setSubscriberEmail(e.target.value)}
                      className="w-full sm:w-48 rounded-full h-9 text-sm"
                      required
                    />
                    <Button
                      type="submit"
                      disabled={subscribing || !subscriberEmail}
                      size="sm"
                      className="rounded-full px-4 h-9 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white"
                    >
                      {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    </Button>
                  </form>
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-6 pt-4 border-t border-border">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2 mb-3">
                  <Share2 className="w-4 h-4" /> Share This Analysis
                </h4>

                <SocialShareBar
                  url={`https://theneuralfeed.com/editorial/${selectedEditorial.id}`}
                  text={buildShareCaption({
                    type: 'editorial',
                    title: selectedEditorial.headline,
                    summary: selectedEditorial.subheadline || selectedEditorial.body?.substring(0, 200),
                    url: `https://theneuralfeed.com/editorial/${selectedEditorial.id}`,
                  })}
                  linkedinText={buildLinkedInCaption({
                    type: 'editorial',
                    title: selectedEditorial.headline,
                    content: selectedEditorial.body,
                    summary: selectedEditorial.subheadline,
                    url: `https://theneuralfeed.com/editorial/${selectedEditorial.id}`,
                  })}
                />
              </div>

              {/* Author Attribution */}
              <div className="mt-4 pt-3 border-t border-border text-center">
                <p className="text-xs text-muted-foreground">
                  ✍️ <span className="font-semibold">The Neural Feed Editorial</span>
                  {selectedEditorial.week_of && <span> · Week of {selectedEditorial.week_of}</span>}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Tool Guide Detail Dialog */}
      <Dialog open={!!selectedToolGuide} onOpenChange={(open) => {
        if (!open) {
          setSelectedToolGuide(null);
          window.history.pushState({}, '', window.location.pathname);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedToolGuide && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className="bg-primary text-primary-foreground font-mono text-[10px]">
                    <Wrench className="w-3 h-3 mr-1" /> TOOL LAB
                  </Badge>
                  <Badge variant="outline" className="font-mono text-xs">{selectedToolGuide.category}</Badge>
                  <Badge variant="outline" className="font-mono text-xs">{selectedToolGuide.difficulty}</Badge>
                  {selectedToolGuide.tested_date && (
                    <Badge variant="secondary" className="font-mono text-[10px]">
                      <Clock className="w-3 h-3 mr-1" /> {selectedToolGuide.tested_date}
                    </Badge>
                  )}
                  {selectedToolGuide.source_article_url && (
                    <a
                      href={selectedToolGuide.source_article_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-muted-foreground hover:text-primary transition-colors underline underline-offset-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Based on news
                    </a>
                  )}
                </div>
                <DialogTitle className="text-lg leading-tight pr-8">
                  {selectedToolGuide.title}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  {selectedToolGuide.tool_name}
                </DialogDescription>
                {selectedToolGuide.tagline && (
                  <p className="text-sm font-medium text-primary mt-2 italic">
                    "{selectedToolGuide.tagline}"
                  </p>
                )}
                {/* Open Tool Button */}
                {selectedToolGuide.tool_url && (
                  <a
                    href={selectedToolGuide.tool_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors w-fit"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open {selectedToolGuide.tool_name}
                  </a>
                )}
              </DialogHeader>

              {/* Overview */}
              <div className="mt-4 space-y-3">
                {selectedToolGuide.overview?.split(/\n\n+/).filter(Boolean).map((para, i) => (
                  <p key={i} className="text-sm leading-relaxed text-foreground">
                    {para.trim()}
                  </p>
                ))}
              </div>

              {/* Verdict Box (Golden Template) */}
              {selectedToolGuide.verdict_box && (
                <div className="mt-4 bg-primary/5 rounded-xl p-5 border-2 border-primary/25">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-primary flex items-center gap-2">
                      <Zap className="w-4 h-4" /> Verdict
                    </h4>
                    {selectedToolGuide.verdict_box.score && (
                      <Badge variant="outline" className="text-emerald-600 border-emerald-500/30 font-bold">
                        ⭐ {selectedToolGuide.verdict_box.score}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2 text-sm leading-relaxed">
                    {selectedToolGuide.verdict_box.best_for && (
                      <div>
                        <span className="font-semibold text-foreground">Best for: </span>
                        <span className="text-muted-foreground">{selectedToolGuide.verdict_box.best_for}</span>
                      </div>
                    )}
                    {selectedToolGuide.verdict_box.skip_if && (
                      <div>
                        <span className="font-semibold text-foreground">Skip if: </span>
                        <span className="text-muted-foreground">{selectedToolGuide.verdict_box.skip_if}</span>
                      </div>
                    )}
                    {selectedToolGuide.verdict_box.price && (
                      <div>
                        <span className="font-semibold text-foreground">Price: </span>
                        <span className="text-muted-foreground">{selectedToolGuide.verdict_box.price}</span>
                      </div>
                    )}
                    {selectedToolGuide.verdict_box.skill_level && (
                      <div>
                        <span className="font-semibold text-foreground">Skill level: </span>
                        <span className="text-muted-foreground">{selectedToolGuide.verdict_box.skill_level}</span>
                      </div>
                    )}
                    {selectedToolGuide.verdict_box.time_to_first_result && (
                      <div>
                        <span className="font-semibold text-foreground">Time to first result: </span>
                        <span className="text-muted-foreground">{selectedToolGuide.verdict_box.time_to_first_result}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Verdict Box */}
              {selectedToolGuide.verdict_box && (
                <div className="mt-4 bg-primary/10 rounded-xl p-5 border border-primary/30">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-primary flex items-center gap-2">
                      <Zap className="w-4 h-4" /> ⚡ VERDICT
                    </h4>
                    {selectedToolGuide.verdict_box.score && (
                      <Badge className="bg-primary text-primary-foreground font-bold text-sm px-3 py-1">
                        ⭐ {selectedToolGuide.verdict_box.score}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2 text-sm leading-relaxed">
                    {selectedToolGuide.verdict_box.best_for && (
                      <div><span className="font-semibold text-foreground">Best for: </span><span className="text-muted-foreground">{selectedToolGuide.verdict_box.best_for}</span></div>
                    )}
                    {selectedToolGuide.verdict_box.skip_if && (
                      <div><span className="font-semibold text-foreground">Skip if: </span><span className="text-muted-foreground">{selectedToolGuide.verdict_box.skip_if}</span></div>
                    )}
                    {selectedToolGuide.verdict_box.price && (
                      <div><span className="font-semibold text-foreground">Price: </span><span className="text-muted-foreground">{selectedToolGuide.verdict_box.price}</span></div>
                    )}
                    {selectedToolGuide.verdict_box.skill_level && (
                      <div><span className="font-semibold text-foreground">Skill level: </span><span className="text-muted-foreground">{selectedToolGuide.verdict_box.skill_level}</span></div>
                    )}
                    {selectedToolGuide.verdict_box.time_to_first_result && (
                      <div><span className="font-semibold text-foreground">Time to first result: </span><span className="text-muted-foreground">{selectedToolGuide.verdict_box.time_to_first_result}</span></div>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Answer Box or Quick Start */}
              {selectedToolGuide.quick_answer_box ? (
                <div className="mt-4 bg-amber-500/10 rounded-xl p-5 border border-amber-500/30">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-amber-600 dark:text-amber-400 flex items-center gap-2">
                      <Zap className="w-4 h-4" /> Quick Answer Box
                    </h4>
                    {selectedToolGuide.quick_answer_box.cta_url && (
                      <a
                        href={selectedToolGuide.quick_answer_box.cta_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors shadow-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {selectedToolGuide.quick_answer_box.cta_label || 'Try Tool'} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <div className="space-y-2 text-sm leading-relaxed">
                    <div>
                      <span className="font-semibold text-foreground">What it does: </span>
                      <span className="text-muted-foreground">{selectedToolGuide.quick_answer_box.what_it_does}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-foreground">Who it's for: </span>
                      <span className="text-muted-foreground">{selectedToolGuide.quick_answer_box.who_its_for}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-foreground">How long it takes: </span>
                      <span className="text-muted-foreground">{selectedToolGuide.quick_answer_box.how_long}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-foreground">Cost & Plan needed: </span>
                      <span className="text-muted-foreground">{selectedToolGuide.quick_answer_box.cost_and_plan}</span>
                    </div>
                  </div>
                </div>
              ) : selectedToolGuide.quick_version && (
                <div className="mt-4 bg-amber-500/5 rounded-lg p-4 border border-amber-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Quick Start
                  </h4>
                  <ul className="space-y-2">
                    {selectedToolGuide.quick_version.split(/\n+/).filter(Boolean).map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-amber-500 font-bold mt-0.5">•</span>
                        <span>{bullet.replace(/^[-•]\s*/, '').trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What You'll Need */}
              {selectedToolGuide.what_you_need?.length > 0 ? (
                <div className="mt-4 bg-blue-500/5 rounded-xl p-5 border border-blue-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-3 flex items-center gap-2">
                    <ListChecks className="w-4 h-4" /> What You'll Need
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {selectedToolGuide.what_you_need.map((item, i) => (
                      <div key={i} className="bg-background/80 rounded-lg p-3 border border-border flex flex-col justify-between">
                        <div>
                          <p className="text-xs font-semibold text-primary">{item.item}</p>
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{item.detail}</p>
                        </div>
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] text-blue-600 dark:text-blue-400 hover:underline mt-2 font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Official Link <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : selectedToolGuide.prerequisites && (
                <div className="mt-4 bg-blue-500/5 rounded-lg p-4 border border-blue-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2 flex items-center gap-2">
                    <ListChecks className="w-4 h-4" /> What You Need
                  </h4>
                  <p className="text-sm leading-relaxed text-foreground/90">{selectedToolGuide.prerequisites}</p>
                </div>
              )}

              {/* Why It Matters */}
              {selectedToolGuide.why_it_matters && (
                <div className="mt-4 bg-primary/10 rounded-lg p-4 border border-primary/30">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-primary mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" /> Why This Matters
                  </h4>
                  <p className="text-sm leading-relaxed text-foreground/90 font-medium">{selectedToolGuide.why_it_matters}</p>
                </div>
              )}

              {/* Key Features */}
              {selectedToolGuide.key_features?.length > 0 && (
                <div className="mt-4 bg-primary/5 rounded-lg p-4 border border-primary/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-primary mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Key Features
                  </h4>
                  <ul className="space-y-2">
                    {selectedToolGuide.key_features.map((feat, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-primary font-bold mt-0.5">•</span>
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Best For */}
              {selectedToolGuide.best_for && (
                <div className="mt-4 bg-blue-500/5 rounded-lg p-4 border border-blue-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Best For
                  </h4>
                  <p className="text-sm leading-relaxed text-foreground/90">{selectedToolGuide.best_for}</p>
                </div>
              )}

              {/* Steps */}
              {selectedToolGuide.steps?.length > 0 && (
                <div className="mt-6 space-y-4">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <ListChecks className="w-4 h-4" /> Step-by-Step Guide
                  </h4>
                  {selectedToolGuide.steps.map((step, i) => {
                    const rawText = step.instruction || step.details || '';
                    const text = rawText.replace(/\[(?:SCREENSHOT\s*TODO|SCREENSHOT|TODO)[^\]]*\]/gi, '').trim();
                    const title = step.title || (text.split('.')[0] + (text.includes('.') ? '.' : ''));
                    const copyText = step.copyable || step.setting_or_prompt || step.code_or_prompt;
                    const actionLabel = step.action ? 'Action' : 'Step';

                    return (
                      <Fragment key={i}>
                        <div className="border border-border rounded-xl overflow-hidden bg-card/50">
                          {/* Step Header */}
                          <div className="flex items-center gap-3 px-4 py-3 bg-muted/40 border-b border-border">
                            <span className="w-7 h-7 rounded-lg bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">
                              {step.step_number || i + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground leading-snug">
                                {title}
                              </p>
                            </div>
                            {step.action && (
                              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded shrink-0 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                {actionLabel}
                              </span>
                            )}
                          </div>

                          {/* Step Body */}
                          <div className="p-4 space-y-3">
                            {step.action && (
                              <p className="text-xs font-medium text-primary">
                                🎯 <span className="underline underline-offset-2">Action:</span> {step.action}
                              </p>
                            )}
                            <p className="text-sm text-foreground/90 leading-relaxed">
                              {text}
                            </p>

                            {/* Step Images */}
                            {step.images?.length > 0 && (
                              <div className="space-y-3 pt-2">
                                {step.images.map((imgObj, imgIdx) => (
                                  <figure key={imgIdx} className="overflow-hidden rounded-lg border border-border bg-muted/20">
                                    <img
                                      src={imgObj.image}
                                      alt={imgObj.alt || title}
                                      className="w-full h-auto object-cover rounded-t-lg"
                                      loading="lazy"
                                    />
                                    {imgObj.caption && (
                                      <figcaption className="p-2 text-xs text-center text-muted-foreground italic bg-muted/40 border-t border-border">
                                        {imgObj.caption}
                                      </figcaption>
                                    )}
                                  </figure>
                                ))}
                              </div>
                            )}

                            {/* Copyable Box (Setting / Prompt) */}
                            {copyText && (
                              <div className="group relative bg-muted/70 rounded-lg p-3 border border-border font-mono text-xs mt-2">
                                <div className="flex items-center justify-between text-[10px] uppercase text-muted-foreground font-sans font-semibold mb-1">
                                  <span>Copyable Prompt / Setting:</span>
                                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-primary">Click to copy</span>
                                </div>
                                <pre className="text-foreground/90 overflow-x-auto whitespace-pre-wrap leading-relaxed pr-8">
                                  <code>{copyText}</code>
                                </pre>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(copyText);
                                    toast.success('Prompt copied to clipboard!');
                                  }}
                                  className="absolute top-2.5 right-2.5 p-1.5 rounded-md bg-background border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shadow-xs"
                                  title="Copy prompt"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Mid-article Subscribe Box after Step 3 */}
                        {i === 2 && (
                          <div className="p-5 rounded-xl bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-sky-500/10 border border-blue-500/30 text-center my-4 space-y-3">
                            <h5 className="text-sm font-bold text-foreground flex items-center justify-center gap-1.5">
                              <span>📬 The week's biggest AI news, free every Monday</span>
                            </h5>
                            <p className="text-xs text-muted-foreground max-w-md mx-auto">
                              The Neural Feed Weekly: 6–8 AI stories in a 5-minute read, plus a page of AI-made comics at the back.
                            </p>
                            <form onSubmit={handleModalSubscribe} className="flex max-w-sm mx-auto gap-2">
                              <input
                                type="email"
                                value={modalSubEmail}
                                onChange={(e) => setModalSubEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                              />
                              <button
                                type="submit"
                                disabled={modalSubscribing}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-xs shrink-0"
                              >
                                {modalSubscribing ? 'Joining...' : "Get Monday's issue"}
                              </button>
                            </form>
                            <p className="text-[10px] text-muted-foreground">Free. Unsubscribe anytime.</p>
                          </div>
                        )}
                      </Fragment>
                    );
                  })}
                </div>
              )}

              {/* Our Real Test */}
              {selectedToolGuide.our_real_test ? (
                <div className="mt-6 bg-slate-950 text-slate-100 rounded-xl p-5 border border-slate-800 shadow-md">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-emerald-400 flex items-center gap-2">
                      <Terminal className="w-4 h-4" /> Our Real Test (Tested Live)
                    </h4>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-emerald-400 border-emerald-500/40 font-mono text-xs">
                        ⏱️ Real Test: {selectedToolGuide.our_real_test.time_taken}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300 mb-3 leading-relaxed">
                    {selectedToolGuide.our_real_test.summary}
                  </p>
                  {selectedToolGuide.our_real_test.baseline_manual_time && (
                    <p className="text-xs text-slate-400 mb-3 italic">
                      Baseline manual time: {selectedToolGuide.our_real_test.baseline_manual_time}
                    </p>
                  )}
                  {selectedToolGuide.our_real_test.diff_output && (
                    <div className="mb-3">
                      <span className="text-[10px] font-mono uppercase text-slate-400 block mb-1">Git Diff Output:</span>
                      <pre className="p-3 bg-black/70 rounded-lg text-xs font-mono text-emerald-300 overflow-x-auto leading-relaxed border border-slate-800">
                        <code>{selectedToolGuide.our_real_test.diff_output}</code>
                      </pre>
                    </div>
                  )}
                  {selectedToolGuide.our_real_test.terminal_output && (
                    <div>
                      <span className="text-[10px] font-mono uppercase text-slate-400 block mb-1">Terminal Verification:</span>
                      <pre className="p-3 bg-black/70 rounded-lg text-xs font-mono text-sky-300 overflow-x-auto leading-relaxed border border-slate-800">
                        <code>{selectedToolGuide.our_real_test.terminal_output}</code>
                      </pre>
                    </div>
                  )}
                </div>
              ) : selectedToolGuide.real_output_example && (
                <div className="mt-6 bg-slate-500/5 rounded-lg p-4 border border-slate-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> What You Get
                  </h4>
                  <div className="space-y-2">
                    {selectedToolGuide.real_output_example.split(/\n\n+/).filter(Boolean).map((para, i) => (
                      <p key={i} className="text-sm leading-relaxed text-foreground/90">{para.trim()}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Table (Golden Template) */}
              {selectedToolGuide.pricing_table?.rows?.length > 0 && (
                <div className="mt-6 bg-muted/30 rounded-xl p-5 border border-border">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-foreground mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-500" /> Pricing (checked {selectedToolGuide.pricing_table.checked})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground">
                          <th className="py-2 pr-3 font-semibold">Plan</th>
                          <th className="py-2 pr-3 font-semibold">Price</th>
                          <th className="py-2 pr-3 font-semibold">What you get</th>
                          <th className="py-2 font-semibold">Link</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {selectedToolGuide.pricing_table.rows.map((row, i) => (
                          <tr key={i} className="hover:bg-muted/30">
                            <td className="py-2.5 pr-3 font-semibold text-foreground">{row[0]}</td>
                            <td className="py-2.5 pr-3 font-mono text-muted-foreground">{row[1]}</td>
                            <td className="py-2.5 pr-3 text-muted-foreground">{row[2]}</td>
                            <td className="py-2.5 font-medium">
                              {row[3] ? (
                                <a href={row[3]} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                                  pricing <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* What Went Wrong / Common Mistakes */}
              {(selectedToolGuide.common_mistakes?.length > 0 || selectedToolGuide.what_went_wrong?.length > 0) && (
                <div className="mt-6 bg-rose-500/5 rounded-xl p-5 border border-rose-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Common Mistakes & Gotchas
                  </h4>
                  <div className="space-y-3">
                    {(selectedToolGuide.common_mistakes || selectedToolGuide.what_went_wrong).map((item, i) => {
                      let prob = '', why = '', fix = '';
                      if (typeof item === 'string') {
                        const parts = item.split('|');
                        prob = parts[0]?.replace('MISTAKE:', '').trim() || item;
                        why = parts[1]?.replace('WHY IT HAPPENS:', '').replace('WHY:', '').trim() || '';
                        fix = parts[2]?.replace('QUICK FIX:', '').replace('FIX:', '').trim() || '';
                      } else if (item && typeof item === 'object') {
                        prob = item.problem || item.mistake || '';
                        why = item.why || '';
                        fix = item.fix || '';
                      }
                      return (
                        <div key={i} className="bg-background/90 rounded-lg p-3.5 border border-border">
                          <p className="text-sm font-semibold text-rose-600 dark:text-rose-400 flex items-start gap-1.5">
                            <span>❌</span> <span>{prob}</span>
                          </p>
                          {why && (
                            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                              <span className="font-medium text-foreground">Why: </span>{why}
                            </p>
                          )}
                          {fix && (
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 font-medium bg-emerald-500/5 p-2 rounded border border-emerald-500/20 flex items-start gap-1.5">
                              <span>✅</span> <span><span className="font-semibold">Quick Fix: </span>{fix}</span>
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Prompts That Work */}
              {selectedToolGuide.prompts_that_work?.length > 0 ? (
                <div className="mt-6 bg-emerald-500/5 rounded-xl p-5 border border-emerald-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-3 flex items-center gap-2">
                    <Terminal className="w-4 h-4" /> Prompts That Work (Tested & Ready to Copy)
                  </h4>
                  <div className="space-y-3">
                    {selectedToolGuide.prompts_that_work.map((p, i) => (
                      <div key={i} className="group relative bg-background/90 rounded-lg p-3 border border-border">
                        <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-1">{p.title}</p>
                        {p.description && <p className="text-xs text-muted-foreground mb-2">{p.description}</p>}
                        <pre className="text-xs font-mono text-foreground/90 overflow-x-auto whitespace-pre-wrap bg-muted/40 p-2.5 rounded border border-border pr-8">
                          <code>{p.prompt}</code>
                        </pre>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(p.prompt);
                            toast.success('Prompt copied!');
                          }}
                          className="absolute top-3 right-3 p-1.5 rounded-md bg-background border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="Copy prompt"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : selectedToolGuide.prompt_library?.length > 0 && (
                <div className="mt-6 bg-emerald-500/5 rounded-lg p-4 border border-emerald-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-3 flex items-center gap-2">
                    <Terminal className="w-4 h-4" /> Prompt Library — Copy & Paste
                  </h4>
                  <div className="space-y-3">
                    {selectedToolGuide.prompt_library.map((prompt, i) => (
                      <div key={i} className="group relative bg-emerald-50 dark:bg-emerald-950/20 rounded-md p-3 border border-emerald-200 dark:border-emerald-800">
                        <p className="text-sm text-foreground/90 leading-relaxed pr-8">{prompt}</p>
                        <button
                          onClick={() => {
                            const textToCopy = prompt.replace(/^PROMPT \d+ — [^:]+:\s*/, '');
                            navigator.clipboard.writeText(textToCopy);
                            toast.success('Prompt copied!');
                          }}
                          className="absolute top-2 right-2 p-1.5 rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-emerald-200 dark:hover:bg-emerald-800"
                          title="Copy prompt"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Limits and Gotchas */}
              {selectedToolGuide.limits_and_gotchas?.length > 0 ? (
                <div className="mt-6 bg-amber-500/5 rounded-xl p-5 border border-amber-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Limits & Gotchas
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedToolGuide.limits_and_gotchas.map((item, i) => (
                      <div key={i} className="bg-background/90 rounded-lg p-3 border border-border">
                        <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">{item.category}</p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : selectedToolGuide.caveats && (
                <div className="mt-4 bg-red-500/5 rounded-lg p-4 border border-red-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-red-600 dark:text-red-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> The Catch
                  </h4>
                  <div className="space-y-2">
                    {selectedToolGuide.caveats.split(/\n\n+/).filter(Boolean).map((para, i) => (
                      <p key={i} className="text-sm leading-relaxed text-foreground/90">{para.trim()}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Alternatives Compared */}
              {Array.isArray(selectedToolGuide.alternatives_compared) && selectedToolGuide.alternatives_compared.length > 0 ? (
                <div className="mt-6 bg-indigo-500/5 rounded-xl p-5 border border-indigo-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-3 flex items-center gap-2">
                    <GitCompare className="w-4 h-4" /> Alternatives Compared
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground">
                          <th className="py-2 pr-3 font-semibold">Tool</th>
                          <th className="py-2 pr-3 font-semibold">Price</th>
                          <th className="py-2 pr-3 font-semibold">Best For</th>
                          <th className="py-2 font-semibold">Verdict</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {selectedToolGuide.alternatives_compared.map((alt, i) => {
                          const altName = alt.name || alt.tool || '';
                          const altLink = alt.source || alt.link || '';
                          const altNotes = alt.notes || alt.verdict || alt.best_for || '';
                          return (
                            <tr key={i} className="hover:bg-muted/30">
                              <td className="py-2.5 pr-3 font-semibold text-foreground">
                                {altLink ? (
                                  <a href={altLink} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
                                    {altName} <ExternalLink className="w-2.5 h-2.5" />
                                  </a>
                                ) : altName}
                              </td>
                              <td className="py-2.5 pr-3 font-mono text-muted-foreground">{alt.price}</td>
                              <td className="py-2.5 pr-3 text-muted-foreground">{alt.plan_tiers || alt.best_for || '-'}</td>
                              <td className="py-2.5 text-foreground/90 font-medium">{altNotes}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : selectedToolGuide.alternatives_compared && typeof selectedToolGuide.alternatives_compared === 'string' && (
                <div className="mt-4 bg-indigo-500/5 rounded-lg p-4 border border-indigo-500/20">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-2 flex items-center gap-2">
                    <GitCompare className="w-4 h-4" /> vs. The Alternatives
                  </h4>
                  <div className="space-y-2">
                    {selectedToolGuide.alternatives_compared.split(/\n\n+/).filter(Boolean).map((para, i) => (
                      <p key={i} className="text-sm leading-relaxed text-foreground/90">{para.trim()}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Pros & Cons */}
              {(selectedToolGuide.pros?.length > 0 || selectedToolGuide.cons?.length > 0) && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectedToolGuide.pros?.length > 0 && (
                    <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
                      <h4 className="font-semibold text-sm uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-2.5 flex items-center gap-1.5">
                        <Check className="w-4 h-4" /> Pros
                      </h4>
                      <ul className="space-y-1.5 text-xs text-foreground/90">
                        {selectedToolGuide.pros.map((p, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="text-emerald-500 font-bold">✓</span>
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedToolGuide.cons?.length > 0 && (
                    <div className="bg-rose-500/10 rounded-xl p-4 border border-rose-500/20">
                      <h4 className="font-semibold text-sm uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-2.5 flex items-center gap-1.5">
                        <AlertCircle className="w-4 h-4" /> Cons
                      </h4>
                      <ul className="space-y-1.5 text-xs text-foreground/90">
                        {selectedToolGuide.cons.map((c, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="text-rose-500 font-bold">✗</span>
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* The Verdict */}
              {selectedToolGuide.verdict && typeof selectedToolGuide.verdict === 'object' ? (
                <div className="mt-6 bg-primary/10 rounded-xl p-5 border border-primary/30">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h4 className="font-semibold text-sm uppercase tracking-wider text-primary flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" /> The Verdict
                    </h4>
                    {selectedToolGuide.verdict.score && (
                      <Badge className="bg-primary text-primary-foreground font-bold text-sm px-3 py-1">
                        ⭐ {selectedToolGuide.verdict.score}
                      </Badge>
                    )}
                  </div>
                  {selectedToolGuide.verdict.summary && (
                    <p className="text-sm leading-relaxed text-foreground/90 mb-4 font-medium">
                      {selectedToolGuide.verdict.summary}
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border/50">
                    <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
                      <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-1 flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5" /> Who Should Use It
                      </p>
                      <p className="text-xs text-foreground/85 leading-relaxed">{selectedToolGuide.verdict.who_should_use}</p>
                    </div>
                    <div className="bg-rose-500/10 rounded-lg p-3 border border-rose-500/20">
                      <p className="text-xs font-semibold text-rose-600 dark:text-rose-400 mb-1 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" /> Who Shouldn't Use It
                      </p>
                      <p className="text-xs text-foreground/85 leading-relaxed">{selectedToolGuide.verdict.who_should_not_use}</p>
                    </div>
                  </div>
                </div>
              ) : selectedToolGuide.verdict && typeof selectedToolGuide.verdict === 'string' && (
                <div className="mt-4 bg-primary/10 rounded-lg p-4 border border-primary/30">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-primary mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> The Verdict
                  </h4>
                  <div className="space-y-2">
                    {selectedToolGuide.verdict.split(/\n\n+/).filter(Boolean).map((para, i) => (
                      <p key={i} className="text-sm leading-relaxed text-foreground/90 font-medium">{para.trim()}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Frequently Asked Questions */}
              {selectedToolGuide.faq?.length > 0 && (
                <div className="mt-6 bg-muted/40 rounded-xl p-5 border border-border">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-foreground mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Frequently Asked Questions
                  </h4>
                  <div className="space-y-3">
                    {selectedToolGuide.faq.map((item, i) => (
                      <div key={i} className="bg-card rounded-lg p-3.5 border border-border/60">
                        <p className="text-sm font-semibold text-foreground mb-1">Q: {item.q || item.question}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{item.a || item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Guides */}
              {selectedToolGuide.related_guides?.length > 0 && (
                <div className="mt-6 bg-muted/20 rounded-xl p-4 border border-border">
                  <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5 text-primary" /> Related Guides
                  </h4>
                  <ul className="space-y-1.5">
                    {selectedToolGuide.related_guides.map((rg, i) => (
                      <li key={i}>
                        <a
                          href={rg.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          👉 {rg.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* End CTA Magazine Banner */}
              <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/30 text-center">
                <p className="text-sm font-semibold text-foreground">
                  📬 The week's biggest AI news, free every Monday 👉 <a href="https://theneuralfeed.com/subscribe" target="_blank" rel="noopener noreferrer" className="text-primary underline font-bold">theneuralfeed.com/subscribe</a>
                </p>
              </div>

              {/* Sources and Updates */}
              {(selectedToolGuide.sources?.length > 0 || selectedToolGuide.sources_and_updates) && (
                <div className="mt-4 flex items-center justify-between flex-wrap gap-2 p-3 bg-muted/20 rounded-lg border border-border text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] font-mono">
                      🛡️ Facts checked {selectedToolGuide.tested_date || selectedToolGuide.sources_and_updates?.last_tested || 'October 2026'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {selectedToolGuide.sources?.map((s, i) => (
                      <a
                        key={i}
                        href={s}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors underline underline-offset-2 text-[11px]"
                      >
                        {s.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]} ↗
                      </a>
                    ))}
                    {selectedToolGuide.sources_and_updates?.official_docs_url && (
                      <a href={selectedToolGuide.sources_and_updates.official_docs_url} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors underline underline-offset-2">
                        Official Docs ↗
                      </a>
                    )}
                    {selectedToolGuide.sources_and_updates?.pricing_url && (
                      <a href={selectedToolGuide.sources_and_updates.pricing_url} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors underline underline-offset-2">
                        Official Pricing ↗
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Related Neural Toon */}
              {selectedToolGuide.related_toon && (
                <div className="mt-6 bg-gradient-to-r from-violet-500/10 via-indigo-500/10 to-primary/10 rounded-xl p-4 border border-violet-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400 flex items-center gap-1.5">
                      <Smile className="w-3.5 h-3.5" /> Related Neural Toon
                    </span>
                    <a href={selectedToolGuide.related_toon.url} className="text-xs text-violet-600 dark:text-violet-400 font-semibold hover:underline flex items-center gap-1">
                      Read Comic <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                  <p className="text-sm font-semibold text-foreground">{selectedToolGuide.related_toon.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{selectedToolGuide.related_toon.caption}</p>
                </div>
              )}

              {/* Subscribe CTA inside Tool Guide Dialog */}
              <div className="mt-6 pt-4 border-t border-border/50 bg-gradient-to-r from-primary/10 to-accent/5 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div className="flex-1 text-center sm:text-left">
                    <h4 className="font-bold text-sm">🚀 Level up your AI toolkit</h4>
                    <p className="text-xs text-muted-foreground">Understand AI in 5 minutes a day. No jargon. No spam. Unsubscribe anytime.</p>
                  </div>
                  <form
                    onSubmit={handleSubscribe}
                    className="flex gap-2 w-full sm:w-auto"
                  >
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={subscriberEmail}
                      onChange={(e) => setSubscriberEmail(e.target.value)}
                      className="w-full sm:w-48 rounded-full h-9 text-sm"
                      required
                    />
                    <Button
                      type="submit"
                      disabled={subscribing || !subscriberEmail}
                      size="sm"
                      className="rounded-full px-4 h-9 bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white"
                    >
                      {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    </Button>
                  </form>
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-6 pt-4 border-t border-border">
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2 mb-3">
                  <Share2 className="w-4 h-4" /> Share This Guide
                </h4>
                <SocialShareBar
                  url={`https://theneuralfeed.com/tool-guide/${selectedToolGuide.id}`}
                  text={buildShareCaption({
                    type: 'toolguide',
                    title: selectedToolGuide.title,
                    summary: selectedToolGuide.why_it_matters || selectedToolGuide.overview?.split(/\n\n+/)[0] || '',
                    url: `https://theneuralfeed.com/tool-guide/${selectedToolGuide.id}`,
                    cta: 'Read the guide',
                  })}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Neural Toons Comic Detail Dialog */}
      <Dialog open={!!selectedComic} onOpenChange={(open) => {
        if (!open) {
          setSelectedComic(null);
          window.history.pushState({}, '', window.location.pathname);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-card/95 backdrop-blur-xl border border-primary/20 text-foreground" data-testid="comic-dialog">
          {selectedComic && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-mono text-[10px] flex items-center gap-1 border-none shadow-sm">
                    <Smile className="w-3 h-3" /> NEURAL TOON
                  </Badge>
                  <Badge variant="outline" className="font-mono text-xs bg-background/50 border-border">{selectedComic.character_pair || 'AI Cast'}</Badge>
                  <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {selectedComic.created_at ? format(new Date(selectedComic.created_at), 'MMMM d, yyyy') : 'Recently'}
                  </span>
                </div>
                <DialogTitle className="text-xl leading-tight pr-8">
                  {selectedComic.headline}
                </DialogTitle>
                {selectedComic.punchline && (
                  <DialogDescription className="text-sm font-semibold text-primary italic leading-relaxed mt-2">
                    💬 "{selectedComic.punchline}"
                  </DialogDescription>
                )}
              </DialogHeader>

              {/* Large Comic Image Frame */}
              <div className="my-4 rounded-lg overflow-hidden border border-border bg-black/40 flex justify-center items-center shadow-inner group relative">
                <img
                  src={selectedComic.public_url}
                  alt={selectedComic.headline}
                  className="max-h-[60vh] object-contain w-full animate-in zoom-in-95 duration-300 select-none"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>

              {/* Character Details / Explanation */}
              {(() => {
                const explanationText = selectedComic.explanation ||
                  (selectedComic.script && [
                    selectedComic.script.panel1?.fact,
                    selectedComic.script.panel2?.fact,
                    selectedComic.script.panel3?.fact,
                    selectedComic.script.panel4?.fact,
                  ].filter(Boolean).join(" ")) ||
                  "";
                if (!explanationText) return null;

                const parts = explanationText.includes('|')
                  ? explanationText.split('|').map(s => s.trim()).filter(Boolean)
                  : null;

                return (
                  <div className="mb-4 bg-muted/40 rounded-xl p-4 border border-border/60 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 font-bold">
                        <Sparkles className="w-3.5 h-3.5 text-accent" /> Context & News Breakdown
                      </h4>
                      {selectedComic.article_title && (
                        <span className="text-[11px] font-mono text-muted-foreground/80 truncate max-w-[280px]">
                          {selectedComic.article_title}
                        </span>
                      )}
                    </div>
                    {parts ? (
                      <div className="space-y-2 mt-2">
                        {parts.map((part, idx) => {
                          const colonIdx = part.indexOf(':');
                          if (colonIdx > 0 && colonIdx < 25) {
                            const label = part.slice(0, colonIdx).trim();
                            const val = part.slice(colonIdx + 1).trim();
                            return (
                              <div key={idx} className="text-xs sm:text-sm flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                                <span className="font-semibold text-foreground/95 whitespace-nowrap">{label}:</span>
                                <span className="text-foreground/80 leading-relaxed">{val}</span>
                              </div>
                            );
                          }
                          return (
                            <p key={idx} className="text-xs sm:text-sm leading-relaxed text-foreground/80">{part}</p>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm leading-relaxed text-foreground/80 font-medium">{explanationText}</p>
                    )}
                  </div>
                );
              })()}

              {/* Sharing & Full Story Actions */}
              <div className="flex flex-wrap items-center justify-between gap-4 mt-6 pt-4 border-t border-border/50">
                {/* Social Sharing buttons */}
                <SocialShareBar
                  url={`https://theneuralfeed.com/comic/${selectedComic.short_id}`}
                  text={buildShareCaption({
                    type: 'comic',
                    title: selectedComic.headline,
                    summary: selectedComic.explanation || selectedComic.punchline || '',
                    url: `https://theneuralfeed.com/comic/${selectedComic.short_id}`,
                  })}
                  imageUrl={selectedComic.public_url}
                />

                {/* Read Associated Article Story Button */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full h-8 px-3 border-border"
                    onClick={() => copyToClipboard(`https://theneuralfeed.com/comic/${selectedComic.short_id}`, 'comic-url')}
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    {copiedField === 'comic-url' ? 'Copied!' : 'Copy Link'}
                  </Button>

                  {selectedComic.article_id && (
                    <Button
                      size="sm"
                      className="rounded-full h-8 px-4 bg-accent hover:bg-accent/90 text-white font-semibold transition-colors"
                      onClick={() => {
                        // Open the associated article modal
                        handleReadArticleForComic(selectedComic.article_id);
                        // Safely close the comic modal
                        setSelectedComic(null);
                        window.history.pushState({}, '', `?article=${selectedComic.article_id}`);
                      }}
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" /> Full Story
                    </Button>
                  )}
                </div>
              </div>

              {/* Subscribe CTA inside Comic Dialog */}
              <div className="mt-6 pt-4 border-t border-border/50 bg-gradient-to-r from-violet-500/10 via-blue-500/10 to-accent/5 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div className="flex-1 text-center sm:text-left">
                    <h4 className="font-bold text-sm">🚀 Get more AI toons in your inbox</h4>
                    <p className="text-xs text-muted-foreground">Understand AI in 5 minutes a day. No jargon. No spam. Unsubscribe anytime.</p>
                  </div>
                  <form 
                    onSubmit={handleSubscribe}
                    className="flex gap-2 w-full sm:w-auto"
                  >
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={subscriberEmail}
                      onChange={(e) => setSubscriberEmail(e.target.value)}
                      className="w-full sm:w-48 rounded-full h-9 text-sm"
                      required
                    />
                    <Button
                      type="submit"
                      disabled={subscribing || !subscriberEmail}
                      size="sm"
                      className="rounded-full px-4 h-9 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white"
                    >
                      {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    </Button>
                  </form>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Neural Snap Detail Dialog */}
      <Dialog open={!!selectedSnap} onOpenChange={(open) => {
        if (!open) {
          setSelectedSnap(null);
          window.history.pushState({}, '', window.location.pathname);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card/95 backdrop-blur-xl border border-emerald-500/30 text-foreground" data-testid="snap-dialog">
          {selectedSnap && (
            <div className="flex flex-col gap-4">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className="bg-emerald-500 text-black font-mono font-bold text-[10px] flex items-center gap-1 border-none shadow-sm">
                    <Zap className="w-3 h-3 fill-current" /> NEURAL SNAP
                  </Badge>
                  {selectedSnap.category && (
                    <Badge variant="outline" className="font-mono text-xs bg-background/50 border-border">{selectedSnap.category}</Badge>
                  )}
                  <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {selectedSnap.created_at ? format(new Date(selectedSnap.created_at), 'MMMM d, yyyy') : 'Today'}
                  </span>
                </div>
                <DialogTitle className="text-xl sm:text-2xl font-bold leading-tight pr-8">
                  {selectedSnap.headline}
                </DialogTitle>
                {selectedSnap.punchline && (
                  <DialogDescription className="text-sm font-semibold text-emerald-400 leading-relaxed mt-2">
                    💥 {selectedSnap.punchline}
                  </DialogDescription>
                )}
              </DialogHeader>

              {/* 4:5 Visual Snap Image Frame (Protected) */}
              <div 
                className="rounded-xl overflow-hidden border border-emerald-500/20 bg-black flex justify-center items-center shadow-2xl relative select-none"
                onContextMenu={(e) => e.preventDefault()}
              >
                <img
                  src={selectedSnap.image_url ? `${selectedSnap.image_url}${selectedSnap.image_url.includes('?') ? '&' : '?'}v=r2` : ''}
                  alt={selectedSnap.headline}
                  className="max-h-[62vh] object-contain w-full animate-in zoom-in-95 duration-300 select-none pointer-events-none"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
                {/* Transparent copy-protection shield overlay */}
                <div 
                  className="absolute inset-0 bg-transparent select-none z-10" 
                  onContextMenu={(e) => e.preventDefault()} 
                  draggable={false} 
                />
              </div>

              {/* Summary and Key Takeaway */}
              {(selectedSnap.summary || selectedSnap.takeaway) && (
                <div className="bg-muted/40 rounded-xl p-4 border border-border/60 shadow-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 font-bold">
                      <Sparkles className="w-3.5 h-3.5 text-emerald-400" /> Story Breakdown
                    </h4>
                    {selectedSnap.source_article_title && (
                      <span className="text-[11px] font-mono text-muted-foreground/80 truncate max-w-[280px]">
                        {selectedSnap.source_article_title}
                      </span>
                    )}
                  </div>
                  {selectedSnap.summary && (
                    <p className="text-sm leading-relaxed text-foreground/90">{selectedSnap.summary}</p>
                  )}
                  {selectedSnap.takeaway && (
                    <div className="pt-2 border-t border-border/40 text-xs sm:text-sm text-emerald-400/90 font-medium">
                      💡 <strong>Key Takeaway:</strong> {selectedSnap.takeaway}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border/50">
                {/* Social Sharing buttons */}
                <SocialShareBar
                  url={`https://theneuralfeed.com/snap/${selectedSnap.id}`}
                  text={buildShareCaption({
                    type: 'snap',
                    title: selectedSnap.headline,
                    summary: selectedSnap.punchline || selectedSnap.summary || selectedSnap.takeaway || '',
                    url: `https://theneuralfeed.com/snap/${selectedSnap.id}`,
                  })}
                  imageUrl={selectedSnap.image_url}
                />

                <div className="flex flex-wrap items-center gap-2">
                  {/* Copy Link */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full h-8 px-3 border-border"
                    onClick={() => copyToClipboard(`https://theneuralfeed.com/snap/${selectedSnap.id}`, 'snap-url')}
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    {copiedField === 'snap-url' ? 'Copied!' : 'Copy Link'}
                  </Button>

                  {/* Send to Telegram (Owner Delivery) */}
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8 px-3 border-sky-500/30 text-sky-400 hover:bg-sky-500/10"
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/snaps/send-telegram/${selectedSnap.id}`);
                          if (res.data?.result?.success) {
                            toast.success('Sent to Telegram successfully!');
                          } else {
                            toast.error('Failed to send to Telegram');
                          }
                        } catch (err) {
                          toast.error('Error sending to Telegram');
                        }
                      }}
                    >
                      <Send className="w-3.5 h-3.5 mr-1" /> Send to Telegram
                    </Button>
                  )}
                </div>

                {/* Read Full Story Button */}
                {(selectedSnap.editorial_id || selectedSnap.article_id || selectedSnap.article_url || (selectedSnap.sources && selectedSnap.sources.length > 0)) && (
                  <Button
                    size="sm"
                    className="rounded-full h-8 px-4 bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-all shadow-md flex items-center gap-1.5"
                    onClick={() => handleReadStoryForSnap(selectedSnap)}
                  >
                    <BookOpen className="w-3.5 h-3.5" /> Read Full Story
                  </Button>
                )}
              </div>

              {/* News Magazine Signup Box inside Snap Dialog */}
              <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/30 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-xs uppercase tracking-wider text-amber-400 font-mono">📬 The week's biggest AI news, free every Monday</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                  The Neural Feed Weekly: 6–8 AI stories in a 5-minute read, plus a page of AI-made comics at the back.
                </p>
                <form onSubmit={handleModalSubscribe} className="flex gap-2">
                  <Input
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={subscriberEmail}
                    onChange={(e) => setSubscriberEmail(e.target.value)}
                    className="h-9 text-xs bg-background/80 border-border"
                  />
                  <Button type="submit" size="sm" className="bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs shrink-0">
                    Get Monday's issue
                  </Button>
                </form>
                <p className="text-[10px] text-muted-foreground mt-2">Free. Unsubscribe anytime.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Footer */}
      <footer className="border-t border-border mt-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          {/* Main footer content */}
          <div className="text-center mb-6">
            <p className="text-xs text-muted-foreground font-mono mb-2">
              THE NEURAL FEED • Independent AI news, every Monday.
            </p>
            <p className="text-[10px] text-muted-foreground">
              Made by Karan, a UK data engineer, with AI-assisted summaries and AI-generated comics.
            </p>
          </div>

          {/* Footer Subscribe CTA */}
          <div className="bg-gradient-to-r from-blue-500/10 via-violet-500/10 to-accent/5 border border-primary/20 rounded-xl p-5 mb-6 max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <h3 className="font-bold text-sm mb-0.5">The week's biggest AI news, free every Monday</h3>
                <p className="text-xs text-muted-foreground">The Neural Feed Weekly: 6–8 AI stories in a 5-minute read, plus comics at the back.</p>
              </div>
              <Button
                size="sm"
                onClick={scrollToSubscribe}
                className="rounded-full px-5 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white cursor-pointer font-medium"
              >
                <Mail className="w-3 h-3 mr-1.5" />
                Get Monday's issue
              </Button>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div className="border-t border-border/30 pt-6 max-w-4xl mx-auto">
            <div className="bg-muted/50 rounded-lg p-4 mb-4">
              <h3 className="text-xs font-semibold text-foreground mb-2 text-center">
                📋 Content Attribution & Fair Use Notice
              </h3>
              <p className="text-[11px] text-muted-foreground leading-relaxed text-center">
                Summaries are written with AI from public RSS feeds and research publications, and every one links to the original. Rights stay with the original publishers.
              </p>
            </div>

            {/* Legal links */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-[10px] text-muted-foreground/70">
              <span>© 2026 The Neural Feed. All rights reserved.</span>
              <span>•</span>
              <span>Fair Use Policy</span>
              <span>•</span>
              <span className="hover:text-accent cursor-pointer transition-colors" title="Send removal requests here">
                Content Removal: contact@theneuralfeed.com
              </span>
              <span>•</span>
              <span>DMCA Compliance</span>
              <span>•</span>
              <a href="/privacy" className="hover:text-accent transition-colors">Privacy Policy</a>
            </div>

            {/* Attribution notice */}
            <p className="text-[9px] text-muted-foreground/60 text-center mt-4 italic">
              We respect intellectual property rights. If you are a publisher and wish to have your content removed,
              please contact us and we will respond within 72 hours.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
// Cache bust v2
